import React, { useState, useEffect, useRef } from "react";
import Nav from "../components/Nav";
import { Logo } from "../components/Logo";
import { LeafBranch, SmallSprig, Blob, FloralCircle, DotCluster, ThinArc } from "../components/Decorations";
import {
  CalendarHeart,
  ShieldCheck,
  Lock,
  Clock3,
  Ear,
  Heart,
  Search,
  Sprout,
  Sun,
  Users,
  Laptop2,
  MapPin,
  Mail,
  MessageCircle,
  Phone,
  Quote,
  Star,
  Plus,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Send,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileText,
  User,
  BadgeCheck,
  MessageSquare,
  GraduationCap,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

import andreaBg from "../assets/hero_junio.jpg";
const HERO_IMG = andreaBg;
import andrea2Bg from "../assets/andrea2.jpg";
const SOBRE_IMG = andrea2Bg;
import andreaContactoBg from "../assets/andrea-contacto.jpg";
const CONTACTO_IMG = andreaContactoBg;
import enfoqueImg from "../assets/enfoque.jpg";
const ENFOQUE_IMG = enfoqueImg;
import contigoImg from "../assets/contigo.jpg";
const CONTIGO_IMG = contigoImg;
import personasImg from "../assets/personas.jpg";
const PERSONAS_IMG = personasImg;
const COZY_IMG =
  "https://images.unsplash.com/photo-1499914485622-a88fac536970?w=900&q=80&auto=format&fit=crop";
const VASE_IMG =
  "https://images.unsplash.com/photo-1521120098171-c1bd7e2c7caf?w=900&q=80&auto=format&fit=crop";
import modalidadesBg from "../assets/modalidades.jpg";
const WRITING_IMG = modalidadesBg;

/* --- HERO --- */
const Hero = () => (
  <section
    id="inicio"
    className="relative overflow-hidden pt-[88px]"
    style={{ background: "linear-gradient(180deg, #E3C9B4 0%, #EDD8C6 45%, #F4E6D8 75%, var(--cream-50) 100%)" }}
  >
    <Blob className="w-[560px] h-[660px] -left-40 top-10 opacity-55 absolute z-0 decor-drift" />
    <Blob className="w-[300px] h-[360px] -right-20 top-[30%] opacity-30 absolute z-0 decor-drift" style={{ animationDelay: "3s" }} />
    <LeafBranch className="absolute left-0 top-[40%] opacity-40 z-0 decor-sway" />
    <LeafBranch className="absolute left-0 top-[10%] opacity-20 z-0 decor-sway" style={{ animationDelay: "1.5s" }} />
    <LeafBranch className="absolute right-0 top-[20%] opacity-15 z-0 decor-sway" flip style={{ animationDelay: "2s" }} />
    <SmallSprig className="absolute left-[42%] bottom-[8%] opacity-25 z-0" />
    <SmallSprig className="absolute right-[15%] bottom-[15%] opacity-20 z-0" />
    <SmallSprig className="absolute left-[20%] top-[18%] opacity-15 z-0" />
    <SmallSprig className="absolute right-[35%] top-[5%] opacity-18 z-0" />
    <FloralCircle className="absolute right-[8%] top-[12%] opacity-30 z-0 decor-spin" />
    <FloralCircle className="absolute left-[30%] top-[3%] opacity-15 z-0" />
    <DotCluster className="absolute right-[28%] bottom-[20%] opacity-40 z-0" />
    <DotCluster className="absolute left-[5%] top-[60%] opacity-30 z-0" />
    <ThinArc className="absolute left-[48%] top-[25%] opacity-20 z-0" />
    <ThinArc className="absolute right-[3%] bottom-[30%] opacity-18 z-0" />

    <div className="max-w-[1320px] mx-auto px-5 lg:px-12 relative z-10 grid lg:grid-cols-[1fr_1.15fr] gap-3 lg:gap-10 items-center pt-3 pb-4 lg:pt-14 lg:pb-10">

      {/* Texto — primero en mobile */}
      <div className="fade-up order-1 lg:order-1">
        <p className="eyebrow mb-1.5 lg:mb-5 text-[0.65rem] lg:text-[0.78rem]" data-testid="hero-eyebrow">
          Terapia online y presencial
        </p>
        <h1
          className="font-serif-display text-[23px] sm:text-[38px] lg:text-[60px] leading-[1.1] text-[var(--ink-900)] mb-2 lg:mb-6"
          data-testid="hero-title"
        >
          Un espacio para
          <br />
          <span className="italic text-[var(--terra-500)]">comprenderte,</span> sanar
          <br />
          y volver a ti
        </h1>
        <p className="text-[var(--ink-500)] text-[12px] lg:text-[16px] leading-[1.65] max-w-[440px] mb-3 lg:mb-8" data-testid="hero-desc">
          Te acompaño en procesos de ansiedad, autoestima, relaciones y cambio personal
          desde una terapia cercana, profunda y respetuosa con tu ritmo.
        </p>
        <div className="flex gap-2 mb-3 lg:mb-8">
          <a href="#contacto"
            className="btn-pill btn-pill-primary lg:py-[0.95rem] lg:px-7 whitespace-nowrap"
            style={{ fontSize: "0.6rem", padding: "6px 10px" }}
            data-testid="hero-cta-agendar">
            <CalendarHeart size={11} />
            Agenda tu sesión
          </a>
          <a href="#enfoque"
            className="btn-pill btn-pill-outline lg:py-[0.95rem] lg:px-7 whitespace-nowrap"
            style={{ fontSize: "0.6rem", padding: "6px 10px" }}
            data-testid="hero-cta-enfoque">
            Mi enfoque
          </a>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 lg:gap-5">
          {[
            { icon: Heart, label: "Terapia cercana" },
            { icon: MapPin, label: "Online y presencial" },
            { icon: Sprout, label: "A tu ritmo" },
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-1 text-[var(--ink-500)] text-[11px] lg:text-[13.5px]">
              <it.icon size={10} className="text-[var(--terra-500)]" strokeWidth={1.6} />
              {it.label}
            </div>
          ))}
        </div>
      </div>

      {/* Imagen + quote card */}
      <div className="order-2 lg:order-2 relative flex justify-center lg:justify-end lg:-mr-6">
        <div className="relative w-full max-w-[600px]">
          <div className="rounded-[20px] lg:rounded-[32px] overflow-hidden shadow-[0_12px_36px_-12px_rgba(59,42,26,0.24)] lg:shadow-[0_30px_70px_-20px_rgba(59,42,26,0.30)] h-[200px] sm:h-[300px] lg:h-[500px]">
            <img
              src={HERO_IMG}
              alt="Psicóloga Andrea Martínez en su consulta"
              className="w-full h-full object-cover object-top"
              style={{ filter: "brightness(1.02) saturate(0.95)" }}
            />
            <div
              className="absolute inset-y-0 left-0 w-24 lg:w-48 pointer-events-none z-10"
              style={{ background: "linear-gradient(to right, rgba(227,201,180,0.85) 0%, rgba(227,201,180,0.40) 60%, transparent 100%)" }}
            />
          </div>
          <div className="hidden lg:block absolute -right-8 bottom-20 w-[180px] bg-white rounded-[18px] p-5 shadow-[0_20px_50px_-15px_rgba(59,42,26,0.22)] z-20">
            <Quote size={18} className="text-[var(--terra-500)] mb-2" strokeWidth={2} />
            <p className="font-serif-display text-[var(--ink-900)] text-[13.5px] leading-snug">
              No tienes que poder con todo a solas.
            </p>
            <div className="mt-3 w-7 h-[2px] bg-[var(--terra-400)] rounded-full" />
          </div>
          <SmallSprig className="hidden lg:block absolute -right-3 bottom-4 opacity-45 z-10" />
        </div>
      </div>
    </div>

    {/* Trust bar — solo escritorio (en mobile ya hay los tags debajo del título) */}
    <div className="max-w-[1200px] mx-auto px-5 lg:px-10 pb-4 lg:pb-12 relative z-10">
      <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-4" data-testid="trust-bar">
        {[
          { icon: Heart, title: "Terapia cercana", desc: "Un acompañamiento humano, empático y sin juicios." },
          { icon: ShieldCheck, title: "Espacio seguro", desc: "Un lugar donde puedes ser tú, hablar con libertad y sentirte comprendido/a." },
          { icon: MapPin, title: "Atención online y presencial", desc: "Tú eliges el formato que mejor se adapta a tu vida." },
        ].map((it, i) => (
          <div key={i} className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-[16px] px-4 py-3 lg:rounded-[20px] lg:px-5 lg:py-5 lg:items-start">
            <div className="icon-circle w-9 h-9 lg:w-12 lg:h-12 shrink-0">
              <it.icon size={16} strokeWidth={1.4} />
            </div>
            <div>
              <h4 className="font-semibold text-[var(--ink-900)] text-[13px] lg:text-[14px] mb-0.5 lg:mb-1">{it.title}</h4>
              <p className="text-[var(--ink-500)] text-[11.5px] lg:text-[12.5px] leading-relaxed hidden sm:block">{it.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --- SOBRE MÍ --- */
const Proceso = () => {
  const especialidades = [
    { icon: "🧠", label: "Ansiedad y Depresión" },
    { icon: "💛", label: "Baja Autoestima" },
    { icon: "🌀", label: "Inestabilidad Emocional y Crisis de Pánico" },
    { icon: "⚡", label: "Descontrol de Impulsos y Agresividad" },
    { icon: "🔍", label: "Dificultades de Memoria y Atención" },
    { icon: "💬", label: "Comunicación Asertiva y Pérdida de Sentido" },
    { icon: "🤝", label: "Abordaje de Consumo de Alcohol y Drogas" },
    { icon: "🌿", label: "Ideación Suicida" },
  ];
  const evaluaciones = [
    { icon: "📋", label: "Evaluaciones Psicológicas Completas con informes" },
    { icon: "🧪", label: "Psicometrías (ej: Cognitivos como Wisc, Wais y otros como Rorschach)" },
    { icon: "🔎", label: "Evaluaciones por déficit atencional" },
    { icon: "💑", label: "Terapia de Parejas" },
    { icon: "👨‍👩‍👧", label: "Integración familiar en procesos terapéuticos" },
  ];
  return (
    <section id="sobre" className="relative py-8 lg:py-12 bg-white border-t border-[var(--cream-200)] overflow-hidden">
      {/* Decoraciones */}
      <Blob className="w-[380px] h-[460px] -right-24 bottom-10 opacity-30 absolute decor-drift" />
      <LeafBranch className="absolute left-0 bottom-16 opacity-25 decor-sway" />
      <LeafBranch className="absolute right-0 top-12 opacity-15 decor-sway" flip style={{ animationDelay: "2s" }} />
      <DotCluster className="absolute right-[10%] top-[15%] opacity-50 decor-pulse" />
      <SmallSprig className="absolute left-[48%] bottom-8 opacity-18" />
      <ThinArc className="absolute left-[6%] top-[12%] opacity-20" />

      <div className="max-w-[1200px] mx-auto px-5 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-14 items-start">

          {/* Foto + datos clave — solo escritorio */}
          <div className="hidden lg:flex flex-col items-start gap-4">
            <div className="relative shrink-0">
              <div className="organic-frame-soft lg:w-[260px] lg:h-[330px]">
                <img
                  src={SOBRE_IMG}
                  alt="Andrea Martínez, psicóloga"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-3 -right-4 bg-white rounded-2xl px-4 py-3 shadow-[0_12px_30px_-10px_rgba(59,42,26,0.18)] flex items-center gap-2">
                <div className="icon-circle w-8 h-8 shrink-0">
                  <ShieldCheck size={13} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[var(--ink-900)] font-semibold text-[12px] leading-tight">15+ años</p>
                  <p className="text-[var(--ink-500)] text-[10.5px]">de experiencia</p>
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col gap-2 mt-2">
              {[
                { icon: Star,    value: "+20 reseñas",         sub: "5 estrellas en Doctoralia" },
                { icon: MapPin,  value: "Providencia · Ñuñoa · Quilpué · Las Condes", sub: "Atención presencial" },
                { icon: Laptop2, value: "Online",              sub: "Videollamada desde donde estés" },
              ].map(({ icon: Ic, value, sub }) => (
                <div key={value} className="flex items-center gap-3 bg-[var(--cream-50)] border border-[var(--cream-200)] rounded-xl px-3.5 py-2.5">
                  <div className="icon-circle w-8 h-8 shrink-0"><Ic size={14} strokeWidth={1.6} /></div>
                  <div>
                    <p className="text-[var(--ink-900)] font-semibold text-[12.5px] leading-tight">{value}</p>
                    <p className="text-[var(--ink-500)] text-[11px]">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contenido */}
          <div>
            {/* Bio compacta solo mobile: avatar pequeño + nombre en línea */}
            <div className="flex lg:hidden items-center justify-between gap-4 mb-4">
              <div className="min-w-0 pr-2">
                <p className="eyebrow text-[0.6rem]">Sobre mí</p>
                <p className="font-serif-display text-[15px] leading-tight text-[var(--ink-900)]">
                  Andrea Martínez<br />
                  <span className="italic text-[var(--terra-500)] text-[13px]">Psicóloga · 15+ años</span>
                </p>
              </div>
              <div className="organic-frame-soft w-[96px] h-[96px] min-[380px]:w-[108px] min-[380px]:h-[108px] shrink-0">
                <img src={SOBRE_IMG} alt="Andrea Martínez" className="w-full h-full object-cover object-top" />
              </div>
            </div>
            <p className="hidden lg:block eyebrow mb-2 lg:mb-3">Sobre mí</p>
            <h2 className="font-serif-display text-[20px] sm:text-[30px] lg:text-[46px] leading-[1.08] text-[var(--ink-900)] mb-2 lg:mb-4">
              <span className="hidden lg:inline">Hola, soy Andrea Martínez,<br /></span>
              <span className="italic text-[var(--terra-500)]">tu psicóloga de confianza.</span>
            </h2>
            <p className="text-[var(--ink-700)] text-[12.5px] lg:text-[15.5px] leading-[1.7] mb-4 lg:mb-7 max-w-[560px]">
              Soy mamá y psicóloga con más de 15 años de experiencia acompañando a personas en sus
              procesos de cambio y bienestar. Soy una profesional flexible, empática, cercana
              y respetuosa de las diferencias socioculturales.
            </p>

            {/* Dos columnas de especialidades */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mb-5 lg:mb-7">
              {/* Columna 1 */}
              <div>
                <h4 className="font-semibold text-[var(--ink-900)] text-[11px] lg:text-[13px] mb-2 flex items-center gap-2 uppercase tracking-wide">
                  <span className="icon-circle w-5 h-5 lg:w-6 lg:h-6"><Star size={10} strokeWidth={1.5} /></span>
                  Áreas de especialidad
                </h4>
                <ul className="space-y-1">
                  {especialidades.map((e, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[var(--ink-700)] text-[12px] lg:text-[13.5px] leading-snug">
                      <span className="text-[12px] leading-none mt-0.5 shrink-0">{e.icon}</span>
                      {e.label}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Columna 2 */}
              <div>
                <h4 className="font-semibold text-[var(--ink-900)] text-[11px] lg:text-[13px] mb-2 flex items-center gap-2 uppercase tracking-wide">
                  <span className="icon-circle w-5 h-5 lg:w-6 lg:h-6"><Search size={10} strokeWidth={1.5} /></span>
                  Evaluaciones y métodos
                </h4>
                <ul className="space-y-1">
                  {evaluaciones.map((e, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[var(--ink-700)] text-[12px] lg:text-[13.5px] leading-snug">
                      <span className="text-[12px] leading-none mt-0.5 shrink-0">{e.icon}</span>
                      {e.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Nota de credenciales */}
            <div className="border-l-2 border-[var(--terra-400)] pl-3 lg:pl-4 py-1 bg-[var(--cream-50)] rounded-r-lg pr-3 lg:pr-4">
              <p className="text-[var(--terra-500)] font-medium text-[12px] lg:text-[14px] leading-relaxed">
                Cuento con Registro en Superintendencia de Salud y Secreduc.<br />
                Atiendo a beneficiarios de Fonasa, Isapres y pacientes particulares.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --- DIPLOMADOS --- */
const Diplomados = () => {
  const diplomados = [
    {
      emoji: "🧩",
      titulo: "Terapia sistémica breve centrada en soluciones",
      institucion: "Adipa",
      grad: "from-[#FBF1E6] to-[#F6E6D5]",
    },
    {
      emoji: "🧠",
      titulo: "Déficit atencional y sus implicancias en las aulas",
      institucion: "Universidad de Valparaíso",
      grad: "from-[#F8EFE2] to-[#F1E5D2]",
    },
    {
      emoji: "💞",
      titulo: "Psicoterapia de pareja desde un enfoque sistémico relacional",
      institucion: "Pontificia Universidad Católica de Chile",
      grad: "from-[#F5EDDF] to-[#EDE2CF]",
    },
  ];

  return (
    <section id="diplomados" className="relative py-8 lg:py-12 bg-white border-t border-[var(--cream-200)] overflow-hidden">
      {/* Decoraciones */}
      <Blob className="w-[340px] h-[420px] -left-24 top-10 opacity-25 absolute decor-drift" />
      <LeafBranch className="absolute right-0 bottom-10 opacity-20 decor-sway" flip />
      <DotCluster className="absolute left-[12%] bottom-[14%] opacity-30 decor-pulse" />
      <FloralCircle className="absolute right-[8%] top-[10%] opacity-12 decor-spin hidden lg:block" />
      <SmallSprig className="absolute right-[6%] bottom-[12%] opacity-16" />
      <ThinArc className="absolute left-[5%] bottom-[10%] opacity-16 hidden lg:block" />

      <div className="max-w-[1200px] mx-auto px-5 lg:px-12 relative z-10">
        {/* Encabezado */}
        <div className="text-center max-w-[620px] mx-auto mb-7 lg:mb-9">
          <p className="eyebrow mb-2">Formación continua</p>
          <h2 className="font-serif-display text-[24px] sm:text-[34px] lg:text-[46px] leading-[1.08] text-[var(--ink-900)] mb-3">
            Diplomados y <span className="italic text-[var(--terra-500)]">especialización</span>
          </h2>
          <p className="text-[var(--ink-500)] text-[13px] lg:text-[14.5px] leading-[1.7]">
            Una formación constante para acompañarte con herramientas actualizadas y un enfoque profesional.
          </p>
        </div>

        {/* Contenido: diplomados (izq, apilados) + cursos (der) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-4 lg:gap-5 items-stretch">

          {/* Diplomados apilados en un solo panel */}
          <div
            className="rounded-2xl p-4 lg:p-5 flex flex-col gap-2.5"
            style={{ background: "linear-gradient(135deg, #F6E9DA 0%, #F1DECB 100%)", border: "1.5px solid var(--peach-200)" }}
          >
            <div className="flex items-center gap-2.5 text-[var(--ink-900)] mb-2">
              <span className="icon-circle w-8 h-8 shrink-0"><GraduationCap size={15} strokeWidth={1.6} /></span>
              <span className="inline-flex flex-col">
                <span className="font-serif-display text-[20px] lg:text-[24px] leading-tight italic">Diplomados</span>
                <span className="mt-1 h-[3px] w-12 rounded-full bg-[var(--terra-500)]" />
              </span>
            </div>
            {diplomados.map((d, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${d.grad} rounded-xl p-3.5 flex items-start gap-3 hover:-translate-y-0.5 transition-transform duration-300`}
                style={{ border: "1.5px solid rgba(255,255,255,0.8)", boxShadow: "0 4px 14px -10px rgba(59,42,26,0.18)" }}
              >
                <span className="w-9 h-9 rounded-xl bg-white/80 border border-white flex items-center justify-center shrink-0 text-[17px] leading-none">
                  {d.emoji}
                </span>
                <div className="min-w-0">
                  <h3 className="font-serif-display text-[15px] lg:text-[16.5px] leading-snug text-[var(--ink-900)]">
                    {d.titulo}
                  </h3>
                  <span className="inline-flex items-center mt-1.5 text-[var(--terra-600)] text-[11px] font-medium leading-snug bg-white/80 rounded-full px-2.5 py-0.5">
                    {d.institucion}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Cursos de actualización */}
          <div className="bg-white rounded-2xl p-4 lg:p-5 border border-[var(--cream-200)] shadow-[0_4px_18px_-10px_rgba(59,42,26,0.18)] flex flex-col gap-3">
            <div className="flex items-center gap-2.5 text-[var(--ink-900)]">
              <span className="icon-circle w-8 h-8 shrink-0"><Star size={14} strokeWidth={1.6} /></span>
              <span className="inline-flex flex-col">
                <span className="font-serif-display text-[20px] lg:text-[24px] leading-tight italic">Cursos de actualización</span>
                <span className="mt-1 h-[3px] w-12 rounded-full bg-[var(--terra-500)]" />
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {[
                { label: "Ansiedad", emoji: "🌿", bg: "#FBF0EC", bd: "#F2DAD0" },
                { label: "Psicometrías", emoji: "🧪", bg: "#F3E9F7", bd: "#E6D4EE" },
                { label: "Déficit atencional", emoji: "🧠", bg: "#E9F1F7", bd: "#D3E2EE" },
                { label: "Comunicación", emoji: "💬", bg: "#E9F6EF", bd: "#D2EBDD" },
                { label: "Coaching para el logro de objetivos personales y profesionales", emoji: "🎯", bg: "#FBEDE4", bd: "#F3DAC9" },
              ].map(({ label, emoji, bg, bd }, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2.5 text-[var(--ink-700)] text-[12px] lg:text-[13px] font-medium leading-snug rounded-xl pl-2.5 pr-3.5 py-2"
                  style={{ background: bg, border: `1.5px solid ${bd}` }}
                >
                  <span className="w-7 h-7 rounded-full bg-white border border-white/80 flex items-center justify-center shrink-0 text-[14px] leading-none shadow-sm">
                    {emoji}
                  </span>
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --- MI ENFOQUE --- */
const Enfoque = () => {
  const pilares = [
    {
      icon: Heart,
      title: "Vínculo genuino y humano",
      desc: "Creo en una relación terapéutica cercana, basada en la confianza y el respeto profundo.",
    },
    {
      icon: Lock,
      title: "Espacio seguro",
      desc: "Un lugar donde puedes ser tú, hablar con empatía en libertad y sentirte comprendido/a.",
    },
    {
      icon: Sun,
      title: "Terapia a tu medida",
      desc: "Adapto herramientas terapéuticas y psicometrías (ej: WISC, WAIS) a tus necesidades.",
    },
    {
      icon: Ear,
      title: "Escucha activa y sin juicio",
      desc: "Cada sesión es un espacio donde tu historia importa y tu proceso es respetado.",
    },
    {
      icon: Sprout,
      title: "Enfoque en el bienestar real",
      desc: "Trabajo contigo en metas concretas y cambios sostenibles para tu vida cotidiana.",
    },
    {
      icon: Laptop2,
      title: "Online y presencial",
      desc: "Tú eliges el formato que mejor se adapta a tu ritmo y estilo de vida.",
    },
  ];

  return (
    <section id="enfoque" className="relative py-4 lg:py-6 overflow-hidden bg-[var(--cream-100)] border-t border-[var(--cream-300)]">
      <LeafBranch className="absolute right-0 top-0 opacity-10 decor-sway" flip />
      <LeafBranch className="absolute left-0 bottom-0 opacity-08 decor-sway" style={{ animationDelay: "3s" }} />
      <DotCluster className="absolute left-[6%] bottom-[16%] opacity-20 decor-pulse" />
      <DotCluster className="absolute right-[12%] top-[30%] opacity-15 decor-pulse" style={{ animationDelay: "2s" }} />
      <FloralCircle className="absolute left-[3%] top-[8%] opacity-12 decor-spin" style={{ animationDelay: "5s" }} />
      <SmallSprig className="absolute left-[55%] top-[6%] opacity-18" />
      <SmallSprig className="absolute right-[28%] bottom-[8%] opacity-16" />
      <ThinArc className="absolute left-[38%] bottom-[5%] opacity-14" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">

        {/* Layout principal: contenido izquierda + imagen derecha */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch">

          {/* Columna izquierda: título + grid */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            {/* Encabezado */}
            <div className="mb-4 text-center lg:text-left">
              <p className="eyebrow mb-2">Mi enfoque</p>
              <h2 className="font-serif-display text-[22px] sm:text-[30px] lg:text-[44px] leading-[1.06] text-[var(--ink-900)] mb-2">
                Cómo trabajo{" "}
                <span className="italic text-[var(--terra-500)]">contigo</span>
              </h2>
              <p className="text-[var(--ink-500)] text-[12.5px] lg:text-[14px] leading-[1.7] max-w-[460px] mx-auto lg:mx-0">
                Un acompañamiento genuino, cálido y profesional para acompañarte en cada etapa de tu proceso.
              </p>
            </div>

            {/* Grid 2×3 de pilares */}
            <div className="flex flex-col gap-1.5 lg:grid lg:grid-cols-2 lg:gap-3">
              {pilares.map((p, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl lg:rounded-2xl px-3 py-2.5 lg:px-5 lg:py-4 flex items-center gap-3 lg:gap-3.5 hover:-translate-y-0.5 transition-all duration-200 group"
                  style={{ border: "1.5px solid var(--cream-300)", boxShadow: "0 2px 10px -4px rgba(59,42,26,0.10)" }}
                >
                  <div className="icon-circle w-8 h-8 lg:w-10 lg:h-10 shrink-0 group-hover:bg-[var(--peach-200)] transition-colors">
                    <p.icon size={14} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-semibold text-[var(--ink-900)] text-[12px] lg:text-[13.5px] leading-snug">{p.title}</h4>
                    <p className="text-[var(--ink-500)] text-[11px] lg:text-[12.5px] leading-relaxed hidden lg:block">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Columna derecha: imagen alta — oculta en mobile pequeño */}
          <div className="hidden sm:flex shrink-0 items-center justify-center lg:justify-end relative">
            {/* Blob de fondo */}
            <div
              className="absolute -top-6 -right-6 w-[270px] h-[430px] bg-[var(--peach-100)] opacity-55"
              style={{ borderRadius: "42px 64px 48px 56px", zIndex: 0 }}
            />
            {/* Imagen principal con difuminado izquierdo */}
            <div
              className="relative z-10 w-[180px] h-[270px] sm:w-[200px] sm:h-[300px] lg:w-[258px] lg:h-[390px] overflow-hidden shadow-[0_24px_56px_-14px_rgba(59,42,26,0.28)]"
              style={{ borderRadius: "38px 56px 44px 52px" }}
            >
              <img
                src={CONTIGO_IMG}
                alt="Andrea Martínez en sesión"
                className="w-full h-full object-cover object-center scale-105"
              />
              {/* Difuminado lateral izquierdo */}
              <div
                className="absolute inset-y-0 left-0 w-[55%] pointer-events-none"
                style={{ background: "linear-gradient(to right, var(--cream-100) 0%, transparent 100%)" }}
              />
            </div>
            {/* Píldora flotante */}
            <div className="absolute bottom-4 -left-4 z-20 bg-white border border-[var(--cream-200)] rounded-full px-3 py-1.5 shadow-sm flex items-center gap-1.5">
              <Heart size={11} fill="#B86A41" className="text-[var(--terra-500)]" />
              <span className="text-[11px] font-semibold text-[var(--ink-700)] whitespace-nowrap">Trato cercano</span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

/* --- TESTIMONIOS --- */
const Testimonios = () => {
  const reviews = [
    {
      name: "Manuel S.",
      initial: "M",
      text: "Dedicada y comprometida con su trabajo. Destaca por su profesionalismo y por explicar cada proceso de forma clara y detallada, lo que genera confianza y facilita mucho la comprensión. Sin duda, una excelente profesional.",
      date: "28 de abril de 2026",
      type: "Terapia de pareja",
    },
    {
      name: "Gi",
      initial: "G",
      text: "Muy buena Profesional, muy atenta y preocupada de seguir cada sesión.",
      date: "17 de abril de 2026",
      type: "Consulta online",
    },
    {
      name: "Sandra Herrera",
      initial: "S",
      text: "Andrea es muy cercana... me da confianza hablar con ella.",
      date: "17 de abril de 2026",
      type: "Terapia individual",
    },
    {
      name: "M.E. C.",
      initial: "M",
      text: "Me he sentido en un espacio seguro para contar toda la vorágine de sentimientos que en este momento de mi vida me están acompañando. Me siento escuchada y comprendida. Me ha ayudado con algunos tips para ir superando cada etapa de este duelo.",
      date: "17 de abril de 2026",
      type: "Consulta online",
    },
    {
      name: "Nataly Quiñones",
      initial: "N",
      text: "Excelente profesional, preocupada y dedicada. Progresivamente he visto un cambio favorable en mi hijo desde que inició las sesiones psicológicas.",
      date: "27 de enero de 2026",
      type: "Consulta psicológica online",
    },
    {
      name: "Desirée Mota",
      initial: "D",
      text: "Excelente profesional. Me atendí con Andrea hace varios años y su acompañamiento fue fundamental. Destaco su calidez, profesionalismo y la claridad con la que guía los procesos terapéuticos. Además, trabajó con gran dedicación con mi hijo menor, ayudándolo a enfocarse, organizar sus ideas y mejorar su desempeño escolar. La recomiendo plenamente.",
      date: "26 de enero de 2026",
      type: "Terapia individual",
    },
    {
      name: "Javiera Galleguillos",
      initial: "J",
      text: "Andrea me acompañó en mi proceso del año pasado con mucha amabilidad y disposición a trabajar. Fue muy asertiva y cercana, eso me hizo sentir como si nos conociéramos hace mucho tiempo. Las sesiones fueron amenas, agradables y ella demostró las herramientas profesionales que tiene. Yo súper que la recomiendo.",
      date: "23 de diciembre de 2025",
      type: "Consulta para Psicología",
    },
    {
      name: "Mabel",
      initial: "M",
      text: "Profesional muy amable y empática. Ofrece buena contención y buena disposición a escuchar y retroalimentar. Me ha ayudado mucho la terapia con Andrea.",
      date: "9 de diciembre de 2025",
      type: "Consulta psicológica online",
    },
    {
      name: "Francisco Rodriguez",
      initial: "F",
      text: "He visto un progreso positivo con mi hija. Es una excelente profesional.",
      date: "9 de diciembre de 2025",
      type: "Consulta psicológica online",
    },
    {
      name: "Kaylee Villarroel",
      initial: "K",
      text: "La psicóloga Andrea siempre fue muy dedicada y respetuosa conmigo. Durante los procesos que viví, me escuchó de verdad, me entendió y me acompañó con mucha empatía. Sus orientaciones y consejos fueron muy importantes para mi bienestar y para poder avanzar. Aprecio profundamente el apoyo que me brindó y el compromiso que mostró en cada sesión.",
      date: "8 de diciembre de 2025",
      type: "Consulta para Psicología",
    },
    {
      name: "Paola Bäuerle",
      initial: "P",
      text: "La psicóloga Andrea demostró compromiso de asistencia y dedicación en cada consulta, asertiva y profesional. Destaco su empatía y capacidad de escucha, elementos que me permitieron acercarme rápidamente al logro de mi recuperación emocional.",
      date: "8 de diciembre de 2025",
      type: "Consulta psicológica online",
    },
    {
      name: "NG",
      initial: "N",
      text: "Andrea es una profesional dedicada, empática y decidida a ayudar profundamente a sus pacientes. Estuvo acompañándonos durante más de un año, brindándonos herramientas para fortalecer nuestros vínculos. Su estilo es cercano y se permite sesiones individuales y grupales cuando se requiere. Gracias por todo.",
      date: "20 de octubre de 2025",
      type: "Consulta psicológica online",
    },
    {
      name: "Katherine Albornoz",
      initial: "K",
      text: "Muy feliz con el acompañamiento de Andrea, agradezco la dedicación y la empatía en las sesiones con mi familia. Los avances han sido significativos.",
      date: "20 de octubre de 2025",
      type: "Consulta para Psicología",
    },
    {
      name: "Valentina",
      initial: "V",
      text: "He tenido una muy buena experiencia con Andrea, es una profesional muy dedicada, empática y comprometida con sus pacientes. Desde el primer día me hizo sentir escuchada y comprendida, creando un espacio seguro donde puedo expresarme sin miedo ni dudas. Gracias a su acompañamiento he logrado avanzar muchísimo en mi proceso personal.",
      date: "10 de octubre de 2025",
      type: "Consulta psicológica online",
    },
    {
      name: "Yeni",
      initial: "Y",
      text: "Súper buena, muy amable y cariñosa. Me agrada cómo busca formas para que mi niño siga el hilo de la conversación y cómo llega a él mediante muchas técnicas. Seguimos en terapia y su progreso ha sido excelente.",
      date: "7 de septiembre de 2025",
      type: "Psicoterapia infantil",
    },
    {
      name: "Nayareth Rodriguez",
      initial: "N",
      text: "Excelente profesional y persona. Andrea es muy dedicada, se da el tiempo de escucharte, jamás te juzga, es comprensiva y abarca muchos puntos de vista distintos. Llegar a ella fue lo mejor que me pudo pasar, no solo es excelente como profesional sino que también es muy humana. La recomiendo totalmente, la experiencia con ella es maravillosa desde la primera sesión.",
      date: "4 de septiembre de 2025",
      type: "Consulta para Psicología",
    },
    {
      name: "Camila Vega",
      initial: "C",
      text: "¡Excelente psicóloga! Realicé terapia de pareja con ella y fue la mejor decisión que pude tomar junto a mi esposo. Literalmente ella ayudó a salvar mi matrimonio. ¡La recomiendo al 1000%!",
      date: "2 de septiembre de 2025",
      type: "Terapia de pareja",
    },
    {
      name: "Patricio Márquez",
      initial: "P",
      text: "Excelente profesional, se da el tiempo de escucharte, de expresar tus ideas y da la confianza para contar las experiencias vividas. Entrega herramientas para mejorar la rutina y las relaciones sociales y laborales. Muy empática y recomendada al 100%.",
      date: "2 de septiembre de 2025",
      type: "Consulta online",
    },
    {
      name: "Lidia",
      initial: "L",
      text: "La experiencia con la psicóloga Andrea ha sido de gran ayuda para mi tratamiento. Excelente en las sesiones, clara y precisa, y lo que más recomiendo es lo cómoda que me sentí hablar con ella en cada sesión. También muy eficaz la atención online, ya que ofrece los horarios que mejor se acomoden a tus tiempos. ¡Excelente, se los recomiendo!",
      date: "2 de septiembre de 2025",
      type: "Consulta psicológica online",
    },
    {
      name: "Tomás Pérez",
      initial: "T",
      text: "Trabajar con ella fue claro y contenedor. Desde la primera sesión creó un espacio seguro y equilibrado, escuchando a ambos por igual y ayudándonos a bajar la tensión. Nos dio herramientas simples y tareas concretas para mejorar la comunicación. En pocas sesiones vimos avances reales: menos reactividad, más empatía y foco en soluciones. La recomiendo por su profesionalismo y efectividad.",
      date: "1 de septiembre de 2025",
      type: "Terapia de pareja",
    },
  ];

  const [activeFilter, setActiveFilter] = useState(null);
  const [filterOpen, setFilterOpen]     = useState(false);
  const filterRef                       = React.useRef(null);
  const [current, setCurrent]   = useState(0);
  const [dir, setDir]           = useState("right");
  const [animKey, setAnimKey]   = useState(0);

  // Tipos únicos ordenados por frecuencia
  const allTypes = reviews.map((r) => r.type);
  const types = [...new Set(allTypes)].sort(
    (a, b) => allTypes.filter((t) => t === b).length - allTypes.filter((t) => t === a).length
  );

  const filtered = activeFilter ? reviews.filter((r) => r.type === activeFilter) : reviews;
  const total = filtered.length;

  // Drag state
  const dragStart    = React.useRef(null);
  const isDragging   = React.useRef(false);
  const [grabbing, setGrabbing] = useState(false);
  const THRESHOLD    = 60; // px mínimos para considerar un swipe

  const go = (newIndex, direction) => {
    setDir(direction);
    setAnimKey((k) => k + 1);
    setCurrent((newIndex + total) % total);
  };

  const prev = () => go(current - 1, "left");
  const next = () => go(current + 1, "right");

  /* ── Mouse handlers ── */
  const onMouseDown = (e) => {
    dragStart.current = e.clientX;
    isDragging.current = true;
    setGrabbing(true);
  };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
  };
  const onMouseUp = (e) => {
    if (!isDragging.current || dragStart.current === null) return;
    const delta = e.clientX - dragStart.current;
    if (Math.abs(delta) >= THRESHOLD) {
      delta < 0 ? next() : prev();
    }
    isDragging.current = false;
    dragStart.current = null;
    setGrabbing(false);
  };
  const onMouseLeave = () => {
    isDragging.current = false;
    dragStart.current = null;
    setGrabbing(false);
  };

  /* ── Touch handlers ── */
  const onTouchStart = (e) => {
    dragStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (dragStart.current === null) return;
    const delta = e.changedTouches[0].clientX - dragStart.current;
    if (Math.abs(delta) >= THRESHOLD) {
      delta < 0 ? next() : prev();
    }
    dragStart.current = null;
  };

  const visibleCount = Math.min(3, total);
  const visible = Array.from({ length: visibleCount }, (_, off) => filtered[(current + off) % total]);

  const selectFilter = (type) => {
    setActiveFilter(type);
    setFilterOpen(false);
    setCurrent(0);
    setAnimKey((k) => k + 1);
    setDir("right");
  };

  React.useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <section id="testimonios" className="relative py-6 lg:py-8 bg-white border-t border-[var(--cream-200)] overflow-hidden">
      <Blob className="w-[380px] h-[460px] -right-24 top-10 opacity-35 absolute decor-drift" />
      <LeafBranch className="absolute left-0 top-1/2 opacity-25 decor-sway" />
      <LeafBranch className="absolute right-0 bottom-10 opacity-18 decor-sway" flip style={{ animationDelay: "2.5s" }} />
      <DotCluster className="absolute left-[40%] top-[4%] opacity-40 decor-pulse" style={{ animationDelay: "1s" }} />
      <ThinArc className="absolute left-[15%] bottom-[10%] opacity-18" />

      <div className="max-w-[1320px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="max-w-[640px] mb-5 lg:mb-7">
          <p className="eyebrow mb-2">Testimonios</p>
          <h2 className="font-serif-display text-[22px] sm:text-[30px] lg:text-[44px] leading-[1.06] text-[var(--ink-900)] mb-2">
            Experiencias reales,{" "}
            <span className="italic text-[var(--terra-500)]">cambios que se sienten.</span>
          </h2>
          <p className="text-[var(--ink-500)] text-[14px] leading-[1.65]">
            Cada proceso es único. Estas son algunas experiencias de personas que decidieron dar el primer paso.
          </p>
        </div>

        {/* Filtro — botón discreto con dropdown */}
        <div className="flex items-center gap-3 mb-5" ref={filterRef}>
          <div className="relative">
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--cream-300)] bg-white text-[var(--ink-700)] text-[12.5px] font-medium hover:border-[var(--terra-400)] hover:text-[var(--terra-500)] transition-all duration-200"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              Filtrar
              {activeFilter && <span className="w-1.5 h-1.5 rounded-full bg-[var(--terra-500)] ml-0.5" />}
              <ChevronRight size={11} className={`transition-transform duration-200 ${filterOpen ? "rotate-90" : "rotate-0"}`} />
            </button>

            {filterOpen && (
              <div className="absolute top-[calc(100%+8px)] left-0 z-50 bg-white border border-[var(--cream-200)] rounded-2xl shadow-[0_12px_32px_-8px_rgba(59,42,26,0.16)] py-1.5 min-w-[200px]">
                <button
                  onClick={() => selectFilter(null)}
                  className={`w-full text-left px-4 py-2 text-[13px] transition-colors ${
                    activeFilter === null
                      ? "text-[var(--terra-500)] font-semibold bg-[var(--cream-50)]"
                      : "text-[var(--ink-700)] hover:bg-[var(--cream-50)] hover:text-[var(--terra-500)]"
                  }`}
                >
                  Todos <span className="text-[var(--ink-400)] font-normal">({reviews.length})</span>
                </button>
                <div className="h-px bg-[var(--cream-100)] mx-3 my-1" />
                {types.map((type) => {
                  const count = reviews.filter((r) => r.type === type).length;
                  return (
                    <button
                      key={type}
                      onClick={() => selectFilter(type)}
                      className={`w-full text-left px-4 py-2 text-[13px] transition-colors ${
                        activeFilter === type
                          ? "text-[var(--terra-500)] font-semibold bg-[var(--cream-50)]"
                          : "text-[var(--ink-700)] hover:bg-[var(--cream-50)] hover:text-[var(--terra-500)]"
                      }`}
                    >
                      {type} <span className="text-[var(--ink-400)] font-normal">({count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Label del filtro activo */}
          {activeFilter && (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--terra-500)] font-medium">
              {activeFilter}
              <button onClick={() => selectFilter(null)} className="text-[var(--terra-400)] hover:text-[var(--terra-600)] leading-none">✕</button>
            </span>
          )}
        </div>

        {/* Carousel */}
        <div className="relative">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 select-none items-stretch"
            style={{ cursor: grabbing ? "grabbing" : "grab", minHeight: 260 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {visible.map((r, i) => (
              <div
                key={`${animKey}-${i}`}
                className={`surface-card flex flex-col border border-[var(--cream-200)] overflow-hidden ${
                  dir === "right" ? "card-slide-right" : "card-slide-left"
                } ${i === 0 ? "flex" : i === 1 ? "hidden sm:flex" : "hidden lg:flex"}`}
                style={{ minHeight: 220, animationDelay: `${i * 60}ms` }}
              >
                {/* Card top accent bar */}
                <div className="h-1 w-full bg-gradient-to-r from-[var(--terra-400)] to-[var(--peach-200)]" />

                <div className="p-5 flex flex-col flex-1">
                  {/* Stars */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} size={15} className="text-[var(--terra-400)]" fill="#B86A41" />
                    ))}
                  </div>

                  {/* Quote + texto */}
                  <div className="mb-4" style={{ minHeight: "9rem" }}>
                    <Quote
                      size={17}
                      className="text-[var(--cream-300)] mb-2"
                      strokeWidth={1.5}
                    />
                    <p
                      className="text-[var(--ink-900)] text-[14px] leading-[1.75] font-[450]"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {r.text}
                    </p>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-[var(--cream-200)] pt-3">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-[var(--peach-100)] border border-[var(--peach-200)] flex items-center justify-center shrink-0">
                        <span className="font-serif-display text-[var(--terra-500)] text-[15px] font-semibold leading-none">
                          {r.initial}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold text-[var(--ink-900)] leading-tight">{r.name}</div>
                        <div className="text-[12px] text-[var(--terra-500)] font-medium mt-0.5 truncate">{r.type}</div>
                        <div className="text-[11px] text-[var(--ink-400)] mt-0.5">{r.date}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation arrows */}
          <div className="flex items-center justify-center gap-5 mt-4">
            <button
              onClick={prev}
              aria-label="Anterior testimonio"
              className="w-11 h-11 rounded-full bg-white border border-[var(--cream-300)] flex items-center justify-center hover:bg-[var(--peach-100)] hover:border-[var(--terra-400)] transition-all duration-200 shadow-sm"
            >
              <ChevronLeft size={18} className="text-[var(--terra-500)]" />
            </button>
            <span className="text-[var(--ink-400)] text-[13px] min-w-[56px] text-center font-medium">
              {current + 1} / {total}
            </span>
            <button
              onClick={next}
              aria-label="Siguiente testimonio"
              className="w-11 h-11 rounded-full bg-white border border-[var(--cream-300)] flex items-center justify-center hover:bg-[var(--peach-100)] hover:border-[var(--terra-400)] transition-all duration-200 shadow-sm"
            >
              <ChevronRight size={18} className="text-[var(--terra-500)]" />
            </button>
          </div>

          {/* Dot indicators — solo los del filtro activo */}
          {total > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {filtered.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i, i > current ? "right" : "left")}
                  aria-label={`Ir al testimonio ${i + 1}`}
                  className={`rounded-full transition-all duration-200 ${
                    i === current
                      ? "w-5 h-2 bg-[var(--terra-500)]"
                      : "w-2 h-2 bg-[var(--cream-300)] hover:bg-[var(--terra-400)]"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-5 text-center">
          <a href="#contacto" className="btn-pill btn-pill-primary">
            <CalendarHeart size={16} />
            Agenda tu primera sesión
          </a>
        </div>
      </div>
    </section>
  );
};

/* --- ESPECIALIDADES --- */
const Especialidades = () => {
  const items = [
    {
      img: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=700&h=420&q=90&auto=format&fit=crop",
      pos: "center 20%",
      title: "Niños",
      d: "Acompaño su mundo emocional con juegos terapéuticos, escucha activa y creatividad. Abordo crisis de ansiedad, dificultades de conducta y apoyo en el desarrollo.",
    },
    {
      img: "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=700&h=420&q=90&auto=format&fit=crop",
      pos: "center 25%",
      title: "Adolescentes",
      d: "Un espacio seguro para explorar identidad, emociones y relaciones. Ayudo con manejo del estrés académico, autoestima y bienestar emocional.",
    },
    {
      img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=700&h=420&q=90&auto=format&fit=crop",
      pos: "center 22%",
      title: "Adultos",
      d: "Exploramos juntos lo que te duele o bloquea, desde ansiedad y depresión hasta crecimiento personal. Herramientas para superar desafíos y encontrar equilibrio.",
    },
    {
      img: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=700&h=420&q=90&auto=format&fit=crop",
      pos: "center 18%",
      title: "Pareja",
      d: "Mejoramos la comunicación, reconstruimos la conexión y resolvemos conflictos con respeto. Ayudo a navegar crisis, infidelidad y fortalecer el vínculo.",
    },
  ];
  return (
    <section id="especialidades" className="relative py-8 lg:py-12 overflow-hidden bg-[var(--cream-100)] border-t border-[var(--cream-300)]">
      <LeafBranch className="absolute right-0 top-1/4 opacity-20 decor-sway" flip />
      <LeafBranch className="absolute left-0 bottom-0 opacity-12 decor-sway" style={{ animationDelay: "2.5s" }} />
      <DotCluster className="absolute right-[6%] bottom-[18%] opacity-30 decor-pulse" style={{ animationDelay: "1.5s" }} />
      <DotCluster className="absolute left-[18%] top-[10%] opacity-22 decor-pulse" style={{ animationDelay: "0.8s" }} />
      <ThinArc className="absolute left-[50%] top-[5%] opacity-15" />
      <ThinArc className="absolute right-[30%] bottom-[6%] opacity-16" />
      <FloralCircle className="absolute left-[5%] top-[40%] opacity-12 decor-spin" style={{ animationDelay: "7s" }} />
      <SmallSprig className="absolute left-[38%] top-[3%] opacity-20" />
      <SmallSprig className="absolute right-[15%] top-[12%] opacity-16" />
      <Blob className="w-[260px] h-[320px] right-[5%] top-[-5%] opacity-15 absolute" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">

        {/* Fila superior: solo texto centrado */}
        <div className="text-center max-w-[640px] mx-auto mb-6 lg:mb-10">
          <div>
            <p className="eyebrow mb-2 lg:mb-3">Especialidades</p>
            <h2 className="font-serif-display text-[24px] sm:text-[36px] lg:text-[54px] leading-[1.06] text-[var(--ink-900)] mb-3 lg:mb-4">
              Acompañamiento
              <br />
              <span className="italic text-[var(--terra-500)]">para cada etapa</span>
              <br />
              de tu vida
            </h2>
            <p className="text-[var(--ink-500)] text-[13.5px] lg:text-[15.5px] leading-[1.7] max-w-[480px] mx-auto">
              Cada etapa trae sus propios desafíos. Te ofrezco un espacio seguro, cercano y adaptado a lo que estás viviendo.
            </p>
          </div>
        </div>

        {/* Fila inferior: 4 cards horizontales */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="speciality-cards">
          {items.map((it, i) => (
            <div
              key={i}
              className="bg-white rounded-xl lg:rounded-2xl overflow-hidden flex flex-col shadow-[0_4px_16px_-6px_rgba(59,42,26,0.10)] border border-[var(--cream-200)] hover:-translate-y-1 hover:shadow-[0_12px_28px_-8px_rgba(59,42,26,0.16)] transition-all duration-200"
              data-testid={`speciality-card-${i}`}
            >
              {/* Imagen superior */}
              <div className="h-[100px] sm:h-[130px] lg:h-[190px] overflow-hidden relative">
                <img
                  src={it.img}
                  alt={it.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{ objectPosition: it.pos, filter: "saturate(0.85) brightness(1.03)" }}
                />
                {/* Degradado inferior para fusionar con el card */}
                <div
                  className="absolute inset-x-0 bottom-0 h-14 pointer-events-none"
                  style={{ background: "linear-gradient(to bottom, transparent 0%, #ffffff 100%)" }}
                />
              </div>
              {/* Contenido */}
              <div className="px-3 py-3 lg:px-4 lg:py-4 flex flex-col gap-1.5 lg:gap-2 flex-1">
                <h3 className="font-serif-display italic text-[15px] lg:text-[20px] text-[var(--ink-900)] leading-tight">{it.title}</h3>
                <p className="text-[var(--ink-500)] text-[11px] lg:text-[12.5px] leading-relaxed flex-1 line-clamp-4 lg:line-clamp-none">{it.d}</p>
                <a
                  href="#contacto"
                  className="text-[9px] lg:text-[10px] tracking-[0.12em] lg:tracking-[0.16em] font-semibold uppercase text-[var(--ink-900)] border border-[var(--ink-900)]/50 px-3 py-1.5 lg:px-4 lg:py-2 rounded-full text-center hover:bg-[var(--ink-900)] hover:text-[var(--cream-50)] transition-all mt-1"
                >
                  Reservar
                </a>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

/* --- MODALIDAD DE ATENCIÓN --- */
const Modalidades = () => {
  const [openMap, setOpenMap] = useState(null);

  const modalidades = [
    {
      id: "online",
      icon: Laptop2,
      title: "Online",
      desc: "Videollamada desde donde estés, con total comodidad y privacidad.",
      tag: "Disponible ahora",
      mapUrl: null,
      ctaHref: "#contacto",
    },
    {
      id: "providencia",
      icon: MapPin,
      title: "Presencial · Providencia",
      desc: "Centro Psicológico Amanece, Avenida Nueva Providencia 2250 Oficina 604, Providencia 7510086.",
      tag: "Santiago",
      location: "Providencia, Chile",
      mapUrl: "https://maps.google.com/maps?q=-33.4217529,-70.6085739&z=17&output=embed",
      mapsLink: "https://www.google.com/maps/search/?api=1&query=-33.4217529,-70.6085739",
    },
    {
      id: "nunoa",
      icon: MapPin,
      title: "Presencial · Ñuñoa",
      desc: "Centro Psicológico Reverie, Avenida Irarrázaval 2821, oficina 1025, Ñuñoa 7750538.",
      tag: "Santiago",
      location: "Ñuñoa, Chile",
      mapUrl: "https://maps.google.com/maps?q=-33.454937,-70.6016464&z=17&output=embed",
      mapsLink: "https://www.google.com/maps/search/?api=1&query=-33.454937,-70.6016464",
    },
    {
      id: "quilpue",
      icon: MapPin,
      title: "Presencial · Quilpué",
      desc: "Serrano 1024, Quilpué.",
      tag: "Valparaíso",
      location: "Quilpué, Chile",
      mapUrl: "https://maps.google.com/maps?q=-33.0495338,-71.4422989&z=17&output=embed",
      mapsLink: "https://www.google.com/maps/search/?api=1&query=-33.0495338,-71.4422989",
    },
    {
      id: "las-condes",
      icon: MapPin,
      title: "Presencial · Las Condes",
      desc: "Consulta presencial en Las Condes. Confirma la dirección exacta al agendar.",
      tag: "Santiago",
      location: "Las Condes, Chile",
      mapUrl: "https://maps.google.com/maps?q=27.503561,-99.5075531&z=17&output=embed",
      mapsLink: "https://www.google.com/maps/search/?api=1&query=27.503561,-99.5075531",
    },
  ];
  const handleModalidadClick = (modalidad) => {
    if (modalidad.ctaHref) {
      document.querySelector(modalidad.ctaHref)?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (!modalidad.mapUrl) return;
    setOpenMap((prev) => (prev === modalidad.id ? null : modalidad.id));
  };

  return (
  <section id="modalidades" className="relative overflow-hidden bg-white border-t border-[var(--cream-200)] py-8 lg:py-20">
    <LeafBranch className="absolute right-0 top-0 opacity-12 decor-sway" flip />
    <LeafBranch className="absolute left-0 bottom-10 opacity-10 decor-sway" style={{ animationDelay: "3.5s" }} />
    <DotCluster className="absolute left-[4%] bottom-[12%] opacity-20 decor-pulse" />
    <DotCluster className="absolute right-[4%] top-[10%] opacity-18 decor-pulse" style={{ animationDelay: "1.2s" }} />
    <FloralCircle className="absolute left-[46%] bottom-[4%] opacity-10 decor-spin" style={{ animationDelay: "4s" }} />
    <SmallSprig className="absolute left-[22%] top-[5%] opacity-18" />
    <ThinArc className="absolute right-[12%] top-[30%] opacity-14" />

    <div className="max-w-[860px] mx-auto px-6 lg:px-10 relative z-10">

      {/* Encabezado centrado */}
      <div className="text-center mb-6 lg:mb-10">
        <p className="eyebrow mb-2 lg:mb-3">Modalidad de atención</p>
        <h2 className="font-serif-display text-[24px] sm:text-[36px] lg:text-[52px] text-[var(--ink-900)] leading-[1.08] mb-3 lg:mb-4">
          Elige la forma que mejor<br />
          <span className="italic text-[var(--terra-500)]">se adapte a ti.</span>
        </h2>
        <p className="text-[var(--ink-500)] text-[13.5px] lg:text-[15.5px] leading-[1.75] max-w-[500px] mx-auto">
          Puedes elegir la modalidad que más se acomode a tu ritmo y estilo de vida.
          Lo importante es que te sientas cómodo/a y puedas comprometerte con tu proceso.
        </p>
      </div>

      {/* Cards de modalidad */}
      <div className="flex flex-col gap-4 mb-10">
        {modalidades.map((m) => (
          <div key={m.id}>
            {/* Fila clickeable */}
            <div
              onClick={() => handleModalidadClick(m)}
              className={`flex items-center gap-3 lg:gap-5 border rounded-2xl px-4 py-4 lg:px-6 lg:py-5 transition-all duration-200 group ${
                m.mapUrl || m.ctaHref ? "cursor-pointer" : "cursor-default"
              } ${
                openMap === m.id
                  ? "bg-[var(--peach-100)] border-[var(--terra-400)] shadow-[0_6px_24px_-8px_rgba(184,106,65,0.22)]"
                  : "bg-[var(--cream-50)] border-[var(--cream-200)] hover:border-[var(--terra-400)] hover:bg-[var(--peach-100)] hover:shadow-[0_4px_16px_-6px_rgba(184,106,65,0.14)]"
              }`}
            >
              <div className={`icon-circle w-10 h-10 lg:w-12 lg:h-12 shrink-0 transition-colors ${openMap === m.id ? "bg-[var(--peach-200)]" : "group-hover:bg-[var(--peach-200)]"}`}>
                <m.icon size={18} strokeWidth={1.4} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <h4 className="font-semibold text-[var(--ink-900)] text-[14px] lg:text-[15px] leading-tight">{m.title}</h4>
                  <span className="text-[10px] text-[var(--terra-500)] bg-[var(--peach-100)] border border-[var(--peach-200)] px-2 py-0.5 rounded-full font-medium shrink-0">{m.tag}</span>
                </div>
                <p className="text-[var(--ink-500)] text-[12.5px] lg:text-[13.5px] leading-snug">{m.desc}</p>
              </div>
              {m.mapUrl && (
                <div className={`shrink-0 flex items-center gap-1 text-[11.5px] lg:text-[12px] font-semibold transition-colors whitespace-nowrap ${openMap === m.id ? "text-[var(--terra-500)]" : "text-[var(--ink-400)] group-hover:text-[var(--terra-500)]"}`}>
                  <span className="hidden sm:inline">{openMap === m.id ? "Ocultar" : "Ver ubicación"}</span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-300 ${openMap === m.id ? "rotate-180" : "rotate-0"}`}
                  />
                </div>
              )}
              {!m.mapUrl && (
                <ChevronRight size={15} className="text-[var(--cream-300)] group-hover:text-[var(--terra-400)] shrink-0 transition-colors" />
              )}
            </div>

            {/* Mapa expandible — protagonista */}
            {m.mapUrl && openMap === m.id && (
              <div className="mt-3 rounded-3xl overflow-hidden border border-[var(--terra-400)] border-opacity-30 shadow-[0_20px_48px_-16px_rgba(59,42,26,0.20)]">
                {/* Header del mapa */}
                <div className="flex items-center gap-3 px-5 py-3.5 bg-[var(--peach-100)] border-b border-[var(--cream-200)]">
                  <div className="icon-circle w-8 h-8 shrink-0 bg-[var(--peach-200)]">
                    <MapPin size={14} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[var(--ink-900)] font-semibold text-[13px] leading-tight">{m.title}</p>
                    <p className="text-[var(--ink-500)] text-[11.5px]">{m.location}</p>
                  </div>
                  <a
                    href={m.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-auto flex items-center gap-1.5 text-[11.5px] font-semibold text-[var(--terra-500)] hover:text-[var(--terra-600)] transition-colors border border-[var(--terra-400)] border-opacity-40 rounded-full px-3 py-1"
                  >
                    <MapPin size={11} strokeWidth={2} />
                    Abrir en Maps
                  </a>
                </div>
                {/* Iframe grande */}
                <iframe
                  src={m.mapUrl}
                  width="100%"
                  height="380"
                  style={{ border: 0, display: "block" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Mapa ${m.title}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* CTA centrado */}
      <div className="flex justify-center">
        <a href="#contacto" className="btn-pill btn-pill-primary">
          <CalendarHeart size={16} />
          Agenda tu sesión
        </a>
      </div>
    </div>
  </section>
  );
};

/* --- PRECIOS --- */
const WaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/* ─────────────────────────────────────────────────────────────
   Precios sincronizados con Google Sheets
   La hoja debe estar publicada / "Cualquiera con el enlace: Lector".
   Estructura esperada de la hoja (fila 1 = encabezados):
     categoria | nombre | precio
   El orden de las categorías se respeta según su primera aparición.
   ───────────────────────────────────────────────────────────── */
const SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/1avWv1V7vKsp1vccKcTEduK7zccmLrkiRAex67n1ruPw/export?format=csv&gid=0";

// Metadatos de presentación por categoría (ícono, badge, color). No vienen de la hoja.
const CAT_META = {
  "Consultas":               { icono: MessageSquare, badge: "Más solicitado", color: { header: "from-[#E8C9B0] to-[#DEBB9E]", icon: "bg-white/40", text: "text-[var(--ink-900)]" } },
  "Psicoterapias":           { icono: Sprout,        badge: null,             color: { header: "from-[#D9C4A8] to-[#CCBA9C]", icon: "bg-white/40", text: "text-[var(--ink-900)]" } },
  "Evaluaciones e informes": { icono: FileText,      badge: null,             color: { header: "from-[#C8B49A] to-[#BDAA8E]", icon: "bg-white/40", text: "text-[var(--ink-900)]" } },
};
const DEFAULT_META = { icono: FileText, badge: null, color: { header: "from-[#C8B49A] to-[#BDAA8E]", icon: "bg-white/40", text: "text-[var(--ink-900)]" } };

// Correcciones de precio aplicadas en código (tienen prioridad sobre la hoja).
const PRICE_OVERRIDES = {
  "Evaluación psicológica pre y post cirugía bariátrica": "$200.000",
};
// Precio fijo por modalidad para las Psicoterapias.
const PSICO_PRECIO = { presencial: "$40.000", online: "$30.000" };

// Precio equivalente en dólares. Valores fijados por la clienta; el resto se calcula.
const CLP_POR_USD = 910;
const USD_FIJO = { 30000: 33, 200000: 215 };
function precioUSD(precio) {
  if (!precio) return null;
  const nums = (precio.match(/\d{1,3}(?:\.\d{3})+/g) || []).map(n => parseInt(n.replace(/\./g, ""), 10));
  if (!nums.length) return null;
  const usd = nums.map(n => USD_FIJO[n] ?? Math.round(n / CLP_POR_USD));
  return usd.length > 1 ? `US$${usd[0]} – US$${usd[usd.length - 1]}` : `US$${usd[0]}`;
}

// Datos de respaldo: se muestran si la hoja no carga o aún no responde.
const GRUPOS_FALLBACK = [
  {
    titulo: "Consultas", ...CAT_META["Consultas"],
    servicios: [
      { nombre: "Consulta psicológica",  precio: "$35.000 – $40.000" },
      { nombre: "Consulta online",       precio: "Desde $25.000" },
      { nombre: "Primera orientación",   precio: "$30.000" },
    ],
  },
  {
    titulo: "Psicoterapias", ...CAT_META["Psicoterapias"],
    servicios: [
      { nombre: "Terapia individual",                        precio: "$35.000 – $40.000" },
      { nombre: "Terapia de pareja",                         precio: "$40.000" },
      { nombre: "Psicoterapia infantil",                     precio: "$40.000" },
      { nombre: "Psicoterapia en adolescentes y adultos",    precio: "$35.000 – $40.000" },
      { nombre: "Terapia para adolescentes",                 precio: "$40.000" },
      { nombre: "Terapia para ansiedad",                     precio: "$40.000" },
      { nombre: "Terapia para depresión",                    precio: "$40.000" },
      { nombre: "Apego",                                     precio: "$40.000" },
      { nombre: "Acoso laboral",                             precio: "$40.000" },
    ],
  },
  {
    titulo: "Evaluaciones e informes", ...CAT_META["Evaluaciones e informes"],
    servicios: [
      { nombre: "Evaluación cognitiva",                                precio: "$150.000" },
      { nombre: "Evaluación neuropsicológica",                         precio: "$150.000" },
      { nombre: "Evaluación por déficit atencional",                   precio: "$150.000" },
      { nombre: "Evaluación psicológica pre y post cirugía bariátrica", precio: "$40.000" },
      { nombre: "Elaboración de informes clínicos",                    precio: "$40.000" },
    ],
  },
];

// Parser de CSV mínimo que respeta comillas y saltos de línea dentro de celdas.
function parseCSV(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (c !== "\r") field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// Convierte las filas del CSV en la estructura de "grupos", agrupando por categoría.
function buildGruposFromCSV(text) {
  const rows = parseCSV(text).filter(r => r.some(c => c.trim() !== ""));
  if (rows.length < 2) return [];
  const header = rows[0].map(h => h.trim().toLowerCase());
  const iCat = header.indexOf("categoria");
  const iNom = header.indexOf("nombre");
  const iPre = header.indexOf("precio");
  if (iCat < 0 || iNom < 0 || iPre < 0) return [];

  const orden = [];
  const mapa = {};
  for (let r = 1; r < rows.length; r++) {
    const cat = (rows[r][iCat] || "").trim();
    const nombre = (rows[r][iNom] || "").trim();
    const precio = (rows[r][iPre] || "").trim();
    if (!cat || !nombre) continue;
    if (!mapa[cat]) { mapa[cat] = []; orden.push(cat); }
    mapa[cat].push({ nombre, precio });
  }
  return orden.map(cat => ({
    titulo: cat,
    ...(CAT_META[cat] || DEFAULT_META),
    servicios: mapa[cat],
  }));
}

// Carga la hoja una sola vez y la reutiliza (precios + formulario de contacto).
let _tarifasPromise = null;
function fetchTarifas() {
  if (_tarifasPromise) return _tarifasPromise;
  _tarifasPromise = fetch(SHEET_CSV_URL, { cache: "no-store" })
    .then(r => (r.ok ? r.text() : Promise.reject(r.status)))
    .then(text => buildGruposFromCSV(text))
    .catch(() => []);
  return _tarifasPromise;
}

// Hook que entrega los grupos de tarifas, con respaldo mientras carga / si falla.
function useTarifas() {
  const [grupos, setGrupos] = useState(GRUPOS_FALLBACK);
  useEffect(() => {
    let cancelado = false;
    fetchTarifas().then(g => { if (!cancelado && g.length) setGrupos(g); });
    return () => { cancelado = true; };
  }, []);
  return grupos;
}

const Precios = () => {
  const grupos = useTarifas();

  const convenios = [
    { icon: Users,      label: "Fonasa" },
    { icon: BadgeCheck, label: "Isapres" },
    { icon: User,       label: "Particular" },
  ];

  const [openGroup, setOpenGroup] = useState(null);
  const [psicoModo, setPsicoModo] = useState("presencial");

  return (
    <section id="precios" className="relative py-7 lg:py-10 overflow-hidden bg-[var(--cream-50)] border-t border-[var(--cream-300)]">
      <LeafBranch className="absolute left-0 top-1/4 opacity-20 decor-sway" />
      <LeafBranch className="absolute right-0 top-1/3 opacity-15 decor-sway" flip style={{ animationDelay: "2s" }} />
      <FloralCircle className="absolute right-[4%] top-[5%] opacity-10 decor-spin" />
      <FloralCircle className="absolute left-[22%] bottom-[8%] opacity-10 decor-spin" style={{ animationDelay: "6s" }} />
      <DotCluster className="absolute left-[42%] top-[3%] opacity-18 decor-pulse" style={{ animationDelay: "0.5s" }} />
      <DotCluster className="absolute right-[22%] bottom-[12%] opacity-15 decor-pulse" style={{ animationDelay: "2.5s" }} />
      <SmallSprig className="absolute left-[8%] bottom-[10%] opacity-20" />
      <SmallSprig className="absolute right-[40%] top-[5%] opacity-16" />
      <ThinArc className="absolute left-[28%] top-[15%] opacity-12" />
      <ThinArc className="absolute right-[8%] bottom-[20%] opacity-14" />
      <Blob className="w-[240px] h-[280px] left-[48%] bottom-0 opacity-20 absolute decor-drift" style={{ animationDelay: "5s" }} />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative z-10">

        {/* Header */}
        <div className="text-center max-w-[560px] mx-auto mb-6">
          <p className="eyebrow mb-2">Tarifas</p>
          <h2 className="font-serif-display text-[24px] sm:text-[34px] lg:text-[50px] leading-[1.06] text-[var(--ink-900)] mb-3">
            Servicios y <span className="italic">precios</span>
          </h2>
          <p className="text-[var(--ink-500)] text-[14px] leading-[1.65]">
            Atiendo a beneficiarios de Fonasa, Isapres y pacientes particulares.
            Cuento con registro en la Superintendencia de Salud y Secreduc.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            {convenios.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[var(--cream-300)] text-[var(--ink-700)] text-[12.5px] font-medium shadow-sm">
                <Icon size={13} className="text-[var(--terra-500)]" strokeWidth={1.8} />
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {grupos.map((g, gi) => {
            const Icon = g.icono;
            const isOpen = openGroup === gi;
            return (
              <div
                key={gi}
                className="rounded-2xl overflow-hidden shadow-[0_8px_32px_-12px_rgba(59,42,26,0.18)] border border-[var(--cream-200)] flex flex-col bg-white transition-shadow duration-300 hover:shadow-[0_12px_36px_-12px_rgba(59,42,26,0.24)]"
              >
                {/* Header coloreado — siempre visible, clickeable */}
                <button
                  onClick={() => setOpenGroup(isOpen ? null : gi)}
                  className={`bg-gradient-to-r ${g.color.header} px-5 py-4 flex items-center gap-3 w-full text-left cursor-pointer`}
                >
                  <div className={`w-9 h-9 rounded-xl ${g.color.icon} border border-white/50 flex items-center justify-center shrink-0`}>
                    <Icon size={18} className="text-[var(--terra-600)]" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1">
                    {g.badge && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--terra-600)] bg-white/60 px-2 py-0.5 rounded-full mb-1">
                        <Star size={9} fill="#A0522D" className="text-[var(--terra-600)]" />
                        {g.badge}
                      </span>
                    )}
                    <h3 className="font-serif-display text-[19px] leading-tight text-[var(--ink-900)]">
                      {g.titulo}
                    </h3>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-[var(--terra-600)] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"}`}
                  />
                </button>

                {/* Prompt visible cuando está cerrado */}
                {!isOpen && (
                  <button
                    onClick={() => setOpenGroup(gi)}
                    className="px-5 py-3.5 flex items-center gap-2 text-[var(--ink-500)] text-[12.5px] hover:text-[var(--terra-500)] transition-colors group"
                  >
                    <span className="w-5 h-5 rounded-full border border-[var(--cream-300)] group-hover:border-[var(--terra-400)] flex items-center justify-center transition-colors">
                      <Plus size={11} strokeWidth={2.5} className="text-[var(--ink-400)] group-hover:text-[var(--terra-500)]" />
                    </span>
                    <span>Toca para ver los precios</span>
                  </button>
                )}

                {/* Lista de servicios — visible al hacer clic */}
                {isOpen && (() => {
                  const esPsico = g.titulo === "Psicoterapias";
                  return (
                    <div className="px-5 py-2 flex flex-col flex-1">
                      {/* Toggle Presencial / Online (solo Psicoterapias) */}
                      {esPsico && (
                        <div className="flex gap-1.5 mb-2 mt-1 p-1 rounded-full bg-[var(--cream-50)] border border-[var(--cream-200)]">
                          {[
                            { id: "presencial", label: "Presencial", icon: MapPin },
                            { id: "online",     label: "Online",     icon: Laptop2 },
                          ].map(({ id, label, icon: MI }) => {
                            const activo = psicoModo === id;
                            return (
                              <button
                                key={id}
                                onClick={() => setPsicoModo(id)}
                                className={`flex-1 flex items-center justify-center gap-1.5 text-[12px] font-medium rounded-full py-1.5 transition-colors ${
                                  activo
                                    ? "bg-white text-[var(--terra-600)] shadow-sm"
                                    : "text-[var(--ink-500)] hover:text-[var(--terra-500)]"
                                }`}
                              >
                                <MI size={13} strokeWidth={1.7} />
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <ul className="flex flex-col flex-1">
                        {g.servicios.map((s, si) => {
                          const precio = esPsico
                            ? PSICO_PRECIO[psicoModo]
                            : (PRICE_OVERRIDES[s.nombre] || s.precio);
                          const usd = precioUSD(precio);
                          const esEvalConSesiones =
                            g.titulo === "Evaluaciones e informes" && /^evaluaci/i.test(s.nombre);
                          return (
                            <li
                              key={si}
                              className={`flex items-start justify-between gap-3 ${esPsico ? "py-2" : "py-2.5"} ${
                                si < g.servicios.length - 1 ? "border-b border-[var(--cream-100)]" : ""
                              }`}
                            >
                              <span className="flex-1">
                                <span className="text-[var(--ink-700)] text-[13px] leading-snug block">{s.nombre}</span>
                                {esEvalConSesiones && (
                                  <span className="text-[var(--ink-400)] text-[11.5px] leading-snug block mt-0.5">
                                    El valor incluye 4 sesiones
                                  </span>
                                )}
                              </span>
                              {esPsico ? (
                                <span className="text-[var(--terra-500)] font-bold text-[13px] shrink-0 whitespace-nowrap">
                                  {precio}{usd ? ` · ${usd}` : ""}
                                </span>
                              ) : (
                                <span className="shrink-0 text-right">
                                  <span className="text-[var(--terra-500)] font-bold text-[13px] block whitespace-nowrap">{precio}</span>
                                  {usd && (
                                    <span className="text-[var(--terra-500)] font-bold text-[13px] block whitespace-nowrap">{usd}</span>
                                  )}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>

        {/* Barra inferior */}
        <div className="mt-5 rounded-2xl border border-[var(--peach-200)] shadow-sm p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" style={{ background: "linear-gradient(135deg, #F1DECB 0%, #EAD0B5 100%)" }}>
          <div className="w-9 h-9 rounded-xl bg-[var(--peach-100)] border border-[var(--peach-200)] flex items-center justify-center shrink-0">
            <ShieldCheck size={16} className="text-[var(--terra-500)]" strokeWidth={1.6} />
          </div>
          <p className="text-[var(--terra-500)] text-[13px] leading-[1.6] flex-1">
            Los precios pueden variar según el tipo de prestación y acuerdo previo.
            Para consultas sobre tarifas o convenios, no dudes en escribirme directamente.
          </p>
          <a
            href="https://wa.me/56995081402"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pill btn-pill-primary shrink-0 flex items-center gap-2 text-[12px] py-2.5 px-5"
          >
            <WaIcon />
            Consultar por WhatsApp
          </a>
        </div>

      </div>
    </section>
  );
};

/* --- FAQ --- */
const FAQ = () => {
  const faqs = [
    { q: "¿Cómo sé si la terapia es para mí?", a: "Si sientes que algo te detiene, te abruma o te genera malestar, la terapia puede ayudarte a comprenderlo y encontrar nuevas formas de avanzar. No necesitas estar en crisis para iniciar un proceso." },
    { q: "¿Cómo son las sesiones y cuánto duran?", a: "Cada sesión dura entre 50 y 60 minutos. Conversamos en un espacio cálido y seguro, abordando lo que te trae a terapia con herramientas prácticas adaptadas a ti." },
    { q: "¿Con qué frecuencia son las sesiones?", a: "Generalmente comenzamos con sesiones semanales para construir el vínculo y avanzar con continuidad. Más adelante podemos espaciarlas según tu proceso." },
    { q: "¿Las sesiones son online o presenciales?", a: "Ofrezco ambas modalidades. Puedes elegir videollamada desde donde estés, o asistir presencialmente en Providencia, Ñuñoa, Quilpué o Las Condes." },
    { q: "¿Cuánto tiempo dura un proceso terapéutico?", a: "Depende de cada persona y de los objetivos del proceso. Algunos procesos son breves y focalizados, otros son más prolongados. Avanzamos juntos a tu ritmo." },
  ];
  return (
    <section id="faq" className="relative py-8 lg:py-10 bg-[var(--cream-100)] border-t border-[var(--cream-300)] overflow-hidden">
      <Blob className="w-[380px] h-[460px] -left-24 bottom-0 opacity-40 absolute decor-drift" />
      <Blob className="w-[280px] h-[340px] -right-16 top-10 opacity-25 absolute decor-drift" style={{ animationDelay: "4s" }} />
      <LeafBranch className="absolute right-0 bottom-20 opacity-30 decor-sway" flip />
      <LeafBranch className="absolute left-0 top-16 opacity-20 decor-sway" style={{ animationDelay: "2s" }} />
      <SmallSprig className="absolute left-[55%] top-10 opacity-20" />
      <SmallSprig className="absolute right-[20%] bottom-12 opacity-22" />
      <FloralCircle className="absolute right-[40%] top-[5%] opacity-18 decor-spin" style={{ animationDelay: "8s" }} />
      <DotCluster className="absolute left-[30%] bottom-[8%] opacity-45 decor-pulse" style={{ animationDelay: "3s" }} />
      <ThinArc className="absolute right-[5%] bottom-[25%] opacity-20" />

      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative z-10">

        {/* Encabezado en la parte superior */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <p className="eyebrow mb-2 lg:mb-3">Preguntas frecuentes</p>
            <h2 className="font-serif-display text-[24px] sm:text-[34px] lg:text-[52px] leading-[1.08] text-[var(--ink-900)]">
              Resolvemos tus<br />
              <span className="italic text-[var(--terra-500)]">dudas más comunes.</span>
            </h2>
          </div>
          <div className="flex flex-col gap-2 lg:text-right lg:items-end shrink-0">
            <p className="text-[var(--ink-500)] text-[14.5px]">
              ¿Tienes otra pregunta?
            </p>
            <a href="#contacto" className="btn-pill btn-pill-outline self-start lg:self-auto">
              <Mail size={14} /> Escríbeme
            </a>
          </div>
        </div>

        {/* Cuerpo: acordeón + imagen */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-10 items-start">
          <Accordion type="single" collapsible className="space-y-2 lg:space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="surface-card border-0 px-4 lg:px-6 data-[state=open]:bg-white"
              >
                <AccordionTrigger className="text-left text-[13.5px] lg:text-[15.5px] font-medium text-[var(--ink-900)] hover:no-underline py-3.5 lg:py-5 [&>svg]:hidden group">
                  <span className="flex-1 pr-3">{f.q}</span>
                  <span className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-[var(--peach-100)] text-[var(--terra-500)] flex items-center justify-center transition-transform group-data-[state=open]:rotate-45 shrink-0">
                    <Plus size={14} />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[var(--ink-500)] text-[13px] lg:text-[14.5px] leading-relaxed pb-3.5 lg:pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Imagen lateral */}
          <div className="hidden lg:block sticky top-[100px] self-start">
            <div className="rounded-[24px] overflow-hidden shadow-[0_20px_48px_-16px_rgba(59,42,26,0.22)]" style={{ height: "300px" }}>
              <img src={COZY_IMG} alt="Espacio cálido" className="w-full h-full object-cover" />
            </div>
            {/* Tarjeta pequeña encima */}
            <div className="mt-4 bg-white rounded-2xl px-5 py-4 shadow-[0_8px_24px_-8px_rgba(59,42,26,0.12)] border border-[var(--cream-200)] flex items-center gap-3">
              <div className="icon-circle w-9 h-9 shrink-0">
                <MessageSquare size={15} strokeWidth={1.5} />
              </div>
              <p className="text-[var(--ink-700)] text-[13px] font-medium leading-snug">
                Consulta sin compromiso.<br />
                <span className="text-[var(--terra-500)] font-semibold">Respondo en menos de 24 h.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --- CONTACTO --- */
const Contacto = () => {
  const [canal, setCanal] = useState(null); // "wa" | "correo"
  const [form, setForm]   = useState({ nombre: "", email: "", telefono: "", modalidad: "", servicio: "", mensaje: "" });
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  /* Lista plana de servicios con precio de referencia, derivada de la misma hoja */
  const grupos = useTarifas();
  const serviciosOpciones = grupos.flatMap((g) => {
    const head = { grupo: `— ${g.titulo} —`, disabled: true, value: "" };
    // Psicoterapias: cada servicio en sus dos modalidades.
    if (g.titulo === "Psicoterapias") {
      return [
        head,
        ...g.servicios.flatMap((s) => [
          { grupo: null, value: `${s.nombre} (Presencial) — ${PSICO_PRECIO.presencial}` },
          { grupo: null, value: `${s.nombre} (Online) — ${PSICO_PRECIO.online}` },
        ]),
      ];
    }
    return [
      head,
      ...g.servicios.map((s) => {
        const precio = PRICE_OVERRIDES[s.nombre] || s.precio;
        return { grupo: null, value: precio ? `${s.nombre} — ${precio}` : s.nombre };
      }),
    ];
  });

  const buildText = () =>
    [`Hola Andrea, te escribo desde tu página web 🌿`,
     `*Nombre:* ${form.nombre}`,
     form.email    ? `*Email:* ${form.email}`              : null,
     form.telefono ? `*Teléfono:* ${form.telefono}`        : null,
     form.modalidad? `*Modalidad:* ${form.modalidad}`      : null,
     form.servicio ? `*Servicio de interés:* ${form.servicio}` : null,
     form.mensaje  ? `\n${form.mensaje}`                   : null,
    ].filter(Boolean).join("\n");

  const [showMailOptions, setShowMailOptions] = useState(false);

  const handleEnviar = () => {
    if (!form.nombre) { toast.error("Por favor ingresa tu nombre"); return; }
    if (canal === "correo" && !form.email) { toast.error("Por favor ingresa tu email"); return; }
    if (canal === "wa") {
      window.open(`https://wa.me/56995081402?text=${encodeURIComponent(buildText())}`, "_blank");
      toast.success("¡Abriendo WhatsApp!");
      setForm({ nombre: "", email: "", telefono: "", modalidad: "", mensaje: "" });
      setCanal(null);
    } else {
      setShowMailOptions(true);
    }
  };

  const doMailto = () => {
    const subj = encodeURIComponent(`Consulta de ${form.nombre} — Página web`);
    const body = encodeURIComponent(buildText());
    window.location.href = `mailto:psandreamartinezlillo@gmail.com?subject=${subj}&body=${body}`;
    setShowMailOptions(false);
    setForm({ nombre: "", email: "", telefono: "", modalidad: "", mensaje: "" });
    setCanal(null);
  };

  const doGmail = () => {
    const subj = encodeURIComponent(`Consulta de ${form.nombre} — Página web`);
    const body = encodeURIComponent(buildText());
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=hola%40andreamartinez.cl&su=${subj}&body=${body}`, "_blank");
    setShowMailOptions(false);
    setForm({ nombre: "", email: "", telefono: "", modalidad: "", mensaje: "" });
    setCanal(null);
  };

  return (
    <section id="contacto" className="relative overflow-hidden border-t border-[var(--cream-200)]"
      style={{ background: "linear-gradient(160deg, #F5EDE3 0%, #EFE3D2 50%, #F1DECB 100%)" }}>

      <LeafBranch className="absolute left-0 top-0 opacity-25 decor-sway" style={{ height: 400 }} />
      <LeafBranch className="absolute right-0 bottom-0 opacity-18 decor-sway" flip style={{ animationDelay: "2s", height: 340 }} />
      <FloralCircle className="absolute left-[44%] top-[4%] opacity-12 decor-spin" style={{ animationDelay: "6s" }} />
      <DotCluster className="absolute left-[5%] bottom-[18%] opacity-22 decor-pulse" style={{ animationDelay: "1s" }} />
      <ThinArc className="absolute right-[8%] top-[28%] opacity-16" />

      <div className="max-w-[1240px] mx-auto px-5 lg:px-12 pt-5 lg:pt-12 pb-6 lg:pb-12 relative z-10">

        {/* Encabezado centrado */}
        <div className="text-center mb-4 lg:mb-9">
          <p className="eyebrow mb-2 lg:mb-4">Contacto</p>
          <h2 className="font-serif-display text-[26px] sm:text-[44px] lg:text-[76px] leading-[1.06] text-[var(--ink-900)] mb-2 lg:mb-5">
            <span className="heading-underline">Hablemos.</span>
            <br />
            <span className="italic text-[var(--terra-500)]">Estoy aquí para</span>
            <br />
            acompañarte.
          </h2>
          <p className="text-[var(--ink-500)] text-[13px] lg:text-[16px] leading-[1.7] max-w-[500px] mx-auto">
            Dar el primer paso puede marcar la diferencia. Escríbeme y encontramos juntas el mejor camino para ti.
          </p>
        </div>

        {/* Cuerpo */}
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 lg:gap-12 items-start">

          {/* Columna izquierda: foto + accesos rápidos — oculta en mobile */}
          <div className="hidden lg:flex flex-col gap-5">
            <div className="relative rounded-[28px] overflow-hidden shadow-[0_28px_64px_-20px_rgba(59,42,26,0.30)]" style={{ height: 400 }}>
              <img src={CONTACTO_IMG} alt="Andrea Martínez, psicóloga"
                className="w-full h-full object-cover" style={{ objectPosition: "center 12%" }} />
              <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
                style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(241,222,203,0.55) 100%)" }} />
              <div className="absolute bottom-5 left-5 bg-white/92 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-[var(--cream-200)] flex items-center gap-2.5">
                <div className="icon-circle w-8 h-8 shrink-0"><Star size={13} strokeWidth={1.5} fill="#B86A41" /></div>
                <div>
                  <p className="text-[var(--ink-900)] font-semibold text-[12px] leading-tight">+20 reseñas 5★</p>
                  <p className="text-[var(--ink-500)] text-[10.5px]">en Doctoralia</p>
                </div>
              </div>
            </div>
            <a href="https://wa.me/56995081402" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 bg-white/80 border border-[var(--cream-200)] rounded-2xl px-5 py-4 hover:border-[#25D366] hover:shadow-[0_4px_18px_-6px_rgba(37,211,102,0.20)] transition-all group">
              <div className="w-11 h-11 rounded-full bg-[#25D366]/10 flex items-center justify-center shrink-0 group-hover:bg-[#25D366]/20 transition-colors">
                <MessageCircle size={20} strokeWidth={1.5} className="text-[#25D366]" />
              </div>
              <div>
                <p className="font-semibold text-[var(--ink-900)] text-[14px]">WhatsApp directo</p>
                <p className="text-[var(--ink-500)] text-[12.5px]">+56 9 9508 1402 · Respuesta rápida</p>
              </div>
              <ChevronRight size={15} className="ml-auto text-[var(--ink-400)] group-hover:text-[#25D366] transition-colors" />
            </a>
            <a href="mailto:psandreamartinezlillo@gmail.com"
              className="flex items-center gap-4 bg-white/80 border border-[var(--cream-200)] rounded-2xl px-5 py-4 hover:border-[var(--terra-400)] hover:shadow-[0_4px_18px_-6px_rgba(184,106,65,0.16)] transition-all group">
              <div className="icon-circle w-11 h-11 shrink-0 group-hover:bg-[var(--peach-200)] transition-colors">
                <Mail size={18} strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-[var(--ink-900)] text-[14px]">Correo electrónico</p>
                <p className="text-[var(--ink-500)] text-[12.5px]">psandreamartinezlillo@gmail.com</p>
              </div>
              <ChevronRight size={15} className="ml-auto text-[var(--ink-400)] group-hover:text-[var(--terra-400)] transition-colors" />
            </a>
          </div>

          {/* Columna derecha: formulario */}
          <div>
            <div className="bg-white rounded-[24px] lg:rounded-[28px] p-5 sm:p-7 lg:p-10 shadow-[0_32px_72px_-24px_rgba(59,42,26,0.20)]" data-testid="contact-form">

              {/* Accesos directos — solo móvil (la columna izquierda está oculta) */}
              <div className="flex lg:hidden gap-2 mb-4">
                <a href="https://wa.me/56995081402" target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl px-2 py-2 text-[11.5px] font-semibold text-[#1a9e50]">
                  <MessageCircle size={13} strokeWidth={1.5} />
                  WhatsApp directo
                </a>
                <a href="mailto:psandreamartinezlillo@gmail.com"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[var(--peach-100)] border border-[var(--peach-200)] rounded-xl px-2 py-2 text-[11.5px] font-semibold text-[var(--terra-600)]">
                  <Mail size={13} strokeWidth={1.5} />
                  Correo directo
                </a>
              </div>

              {/* Paso 1 — elegir canal */}
              <h3 className="font-serif-display text-[17px] lg:text-[28px] text-[var(--ink-900)] mb-0.5">
                Cuéntame cómo puedo
                <span className="italic text-[var(--terra-500)]"> ayudarte</span>
              </h3>
              <p className="text-[var(--ink-500)] text-[12px] lg:text-[13px] mb-3">¿Cómo prefieres que me ponga en contacto contigo?</p>

              <div className="grid grid-cols-2 gap-2.5 mb-4 lg:mb-7">
                {/* Opción WhatsApp */}
                <button
                  type="button"
                  onClick={() => setCanal("wa")}
                  className={`flex flex-row lg:flex-col items-center gap-2 lg:gap-2 rounded-xl lg:rounded-2xl border-2 py-3 lg:py-4 px-3 transition-all ${
                    canal === "wa"
                      ? "border-[#25D366] bg-[#25D366]/8 shadow-[0_4px_16px_-6px_rgba(37,211,102,0.25)]"
                      : "border-[var(--cream-200)] bg-[var(--cream-50)] hover:border-[#25D366]/50"
                  }`}
                >
                  <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-colors shrink-0 ${canal === "wa" ? "bg-[#25D366]/15" : "bg-[var(--peach-100)]"}`}>
                    <MessageCircle size={15} strokeWidth={1.5} className={canal === "wa" ? "text-[#25D366]" : "text-[var(--terra-500)]"} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-[12.5px] lg:text-[13px] text-[var(--ink-900)]">WhatsApp</span>
                    {canal === "wa" && <span className="text-[10px] text-[#25D366] font-medium">✓ Seleccionado</span>}
                  </div>
                </button>

                {/* Opción Correo */}
                <button
                  type="button"
                  onClick={() => setCanal("correo")}
                  className={`flex flex-row lg:flex-col items-center gap-2 lg:gap-2 rounded-xl lg:rounded-2xl border-2 py-3 lg:py-4 px-3 transition-all ${
                    canal === "correo"
                      ? "border-[var(--terra-400)] bg-[var(--peach-100)] shadow-[0_4px_16px_-6px_rgba(184,106,65,0.22)]"
                      : "border-[var(--cream-200)] bg-[var(--cream-50)] hover:border-[var(--terra-400)]/50"
                  }`}
                >
                  <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-colors shrink-0 ${canal === "correo" ? "bg-[var(--peach-200)]" : "bg-[var(--peach-100)]"}`}>
                    <Mail size={15} strokeWidth={1.5} className="text-[var(--terra-500)]" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-[12.5px] lg:text-[13px] text-[var(--ink-900)]">Correo</span>
                    {canal === "correo" && <span className="text-[10px] text-[var(--terra-500)] font-medium">✓ Seleccionado</span>}
                  </div>
                </button>
              </div>

              {/* Paso 2 — formulario (aparece al elegir canal) */}
              {canal && (
                <div style={{ animation: "fadeUp 0.35s ease both" }}>
                  <div className="grid sm:grid-cols-2 gap-2 lg:gap-3 mb-2 lg:mb-3">
                    {/* Nombre — siempre requerido */}
                    <Input placeholder="Nombre completo *" value={form.nombre} onChange={upd("nombre")}
                      className="h-10 lg:h-12 text-[13px] bg-[var(--cream-50)] border-[var(--cream-200)] rounded-xl" />

                    {/* Email */}
                    <div className="relative">
                      <Input
                        type="email"
                        placeholder={canal === "wa" ? "Email (opcional)" : "Email *"}
                        value={form.email}
                        onChange={upd("email")}
                        className={`h-10 lg:h-12 text-[13px] bg-[var(--cream-50)] rounded-xl ${canal === "wa" ? "border-[var(--cream-200)] opacity-75" : "border-[var(--terra-400)]/40"}`}
                      />
                    </div>

                    {/* Teléfono */}
                    <div className="relative">
                      <Input
                        placeholder={canal === "correo" ? "Teléfono (opcional)" : "Teléfono / WhatsApp *"}
                        value={form.telefono}
                        onChange={upd("telefono")}
                        className={`h-10 lg:h-12 text-[13px] bg-[var(--cream-50)] rounded-xl ${canal === "correo" ? "border-[var(--cream-200)] opacity-75" : "border-[var(--terra-400)]/40"}`}
                      />
                    </div>

                    <Select onValueChange={(v) => setForm((p) => ({ ...p, modalidad: v }))}>
                      <SelectTrigger className="h-10 lg:h-12 text-[13px] bg-[var(--cream-50)] border-[var(--cream-200)] rounded-xl text-[var(--ink-500)]">
                        <SelectValue placeholder="¿Cómo te gustaría agendar?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Online">Online</SelectItem>
                        <SelectItem value="Presencial — Providencia">Presencial — Providencia</SelectItem>
                        <SelectItem value="Presencial — Ñuñoa">Presencial — Ñuñoa</SelectItem>
                        <SelectItem value="Presencial — Quilpué">Presencial — Quilpué</SelectItem>
                        <SelectItem value="Presencial — Las Condes">Presencial — Las Condes</SelectItem>
                        <SelectItem value="Solo tengo una duda">Solo tengo una duda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selector de servicio */}
                  <div className="sm:col-span-2 mb-1">
                    <Select onValueChange={(v) => setForm((p) => ({ ...p, servicio: v }))}>
                      <SelectTrigger className="h-10 lg:h-12 text-[13px] w-full bg-[var(--cream-50)] border-[var(--cream-200)] rounded-xl text-[var(--ink-500)]">
                        <SelectValue placeholder="Servicio de interés (opcional)" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {serviciosOpciones.map((op, i) =>
                          op.disabled ? (
                            <div key={i} className="px-3 pt-3 pb-1 text-[10.5px] font-bold tracking-[0.14em] uppercase text-[var(--terra-500)] select-none">
                              {op.grupo}
                            </div>
                          ) : (
                            <SelectItem key={i} value={op.value} className="text-[13px]">
                              {op.value}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <Textarea
                    placeholder="Cuéntame brevemente en qué puedo acompañarte..."
                    rows={3}
                    value={form.mensaje}
                    onChange={upd("mensaje")}
                    className="text-[13px] bg-[var(--cream-50)] border-[var(--cream-200)] rounded-xl mb-3 lg:mb-5 mt-2 lg:mt-3"
                  />

                  {/* Botón único según canal */}
                  <button
                    type="button"
                    onClick={handleEnviar}
                    className="btn-pill w-full justify-center py-3 lg:py-4 text-[0.82rem] lg:text-[0.85rem] font-bold text-white transition-all hover:-translate-y-0.5 mb-3 lg:mb-4"
                    style={canal === "wa"
                      ? { background: "#25D366", boxShadow: "0 10px 28px -10px rgba(37,211,102,0.55)" }
                      : { background: "var(--terra-500)", boxShadow: "0 10px 28px -10px rgba(184,106,65,0.55)" }
                    }
                  >
                    {canal === "wa"
                      ? <><MessageCircle size={16} strokeWidth={2} /> Enviar por WhatsApp</>
                      : <><Send size={15} /> Enviar por correo</>
                    }
                  </button>

                  <div className="flex items-center justify-center gap-2 text-[var(--ink-400)] text-[11.5px]">
                    <Lock size={12} />
                    <span>Tu información es confidencial y está segura.</span>
                  </div>
                </div>
              )}

              {/* Estado inicial — sin canal seleccionado */}
              {!canal && (
                <p className="text-center text-[var(--ink-400)] text-[13px] py-2">
                  Elige una opción arriba para continuar →
                </p>
              )}
            </div>

            {/* Garantía */}
            <div className="mt-4 flex items-center gap-4 bg-white/60 backdrop-blur-sm border border-[var(--cream-200)] rounded-2xl px-5 py-4">
              <div className="icon-circle w-10 h-10 shrink-0"><ShieldCheck size={17} strokeWidth={1.5} /></div>
              <p className="text-[var(--ink-700)] text-[13px] leading-snug">
                <span className="font-semibold">Confidencialidad y respeto, siempre.</span><br />
                <span className="text-[var(--ink-500)]">Todo lo que compartas será tratado con el máximo cuidado.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal: elegir cómo abrir el correo ── */}
      {showMailOptions && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          style={{ background: "rgba(59,42,26,0.45)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowMailOptions(false)}
        >
          <div
            className="bg-white rounded-[28px] p-8 max-w-[380px] w-full shadow-[0_32px_80px_-20px_rgba(59,42,26,0.40)]"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "fadeUp 0.3s ease both" }}
          >
            <h4 className="font-serif-display text-[22px] text-[var(--ink-900)] mb-1 text-center">
              ¿Cómo quieres enviar<br />
              <span className="italic text-[var(--terra-500)]">el correo?</span>
            </h4>
            <p className="text-[var(--ink-500)] text-[13px] text-center mb-6">
              El mensaje ya está redactado. Solo elige tu plataforma.
            </p>

            <div className="flex flex-col gap-3">
              {/* Gmail */}
              <button
                onClick={doGmail}
                className="flex items-center gap-4 w-full rounded-2xl border border-[var(--cream-200)] bg-[var(--cream-50)] px-5 py-4 hover:border-[#EA4335] hover:bg-red-50/40 transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-white border border-[var(--cream-200)] flex items-center justify-center shrink-0 shadow-sm">
                  {/* Gmail G */}
                  <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                    <path d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.2 6.9 29.4 5 24 5 12.9 5 4 13.9 4 25s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z" fill="#FFC107"/>
                    <path d="M6.3 14.7l6.6 4.8C14.7 16 19.1 13 24 13c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.2 6.9 29.4 5 24 5c-7.7 0-14.4 4.4-17.7 10.7z" fill="#FF3D00"/>
                    <path d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 36.6 26.7 37.5 24 37.5c-5.3 0-9.7-3.3-11.3-7.9l-6.6 5.1C9.5 40.5 16.2 45 24 45z" fill="#4CAF50"/>
                    <path d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.1 5.4l6.2 5.2C37 38.1 44 33 44 25c0-1.3-.1-2.6-.4-3.9z" fill="#1976D2"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-[var(--ink-900)] text-[14px]">Abrir con Gmail</p>
                  <p className="text-[var(--ink-500)] text-[12px]">Se abre en el navegador</p>
                </div>
                <ChevronRight size={15} className="ml-auto text-[var(--ink-400)] group-hover:text-[#EA4335] transition-colors" />
              </button>

              {/* Cliente de correo local */}
              <button
                onClick={doMailto}
                className="flex items-center gap-4 w-full rounded-2xl border border-[var(--cream-200)] bg-[var(--cream-50)] px-5 py-4 hover:border-[var(--terra-400)] hover:bg-[var(--peach-100)] transition-all group"
              >
                <div className="icon-circle w-10 h-10 shrink-0">
                  <Mail size={17} strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-[var(--ink-900)] text-[14px]">Correo del dispositivo</p>
                  <p className="text-[var(--ink-500)] text-[12px]">Outlook, Apple Mail, etc.</p>
                </div>
                <ChevronRight size={15} className="ml-auto text-[var(--ink-400)] group-hover:text-[var(--terra-400)] transition-colors" />
              </button>
            </div>

            <button
              onClick={() => setShowMailOptions(false)}
              className="mt-5 w-full text-center text-[12.5px] text-[var(--ink-400)] hover:text-[var(--ink-700)] transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

/* --- FOOTER --- */
const Footer = () => (
  <footer className="pt-6 lg:pt-8 pb-4" style={{ background: "linear-gradient(160deg, #2C1A10 0%, #3B2A1A 100%)" }}>

    {/* ── MÓVIL: layout compacto ── */}
    <div className="lg:hidden max-w-[1320px] mx-auto px-5 pb-5">
      {/* Fila superior: logo + redes */}
      <div className="flex items-center justify-between mb-4">
        <Logo light />
        <div className="flex gap-2">
          {[
            { href: "https://www.instagram.com/psico.andreaml/", icon: <Instagram size={14} />, label: "Instagram" },
            { href: "https://www.doctoralia.cl/andrea-martinez-lillo/psicologo/quilpue", icon: <span className="font-bold text-[11px]">Dr</span>, label: "Doctoralia" },
          ].map(({ href, icon, label }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-[var(--terra-500)] text-white flex items-center justify-center hover:bg-[var(--terra-400)] transition-colors" aria-label={label}>
              {icon}
            </a>
          ))}
        </div>
      </div>

      {/* Fila: nav en 2 columnas + contacto */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-0 mb-4">
        <div>
          <h5 className="font-semibold text-[var(--cream-50)] text-[11px] uppercase tracking-wider mb-2">Navegación</h5>
          <ul className="space-y-1.5 text-[12.5px] text-[var(--cream-200)]">
            {[
              { label: "Inicio", id: "inicio" },
              { label: "Sobre mí", id: "sobre" },
              { label: "Mi enfoque", id: "enfoque" },
              { label: "Testimonios", id: "testimonios" },
              { label: "Especialidades", id: "especialidades" },
            ].map((t) => (
              <li key={t.id}><a href={`#${t.id}`} className="hover:text-[var(--terra-400)] transition-colors">{t.label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h5 className="font-semibold text-[var(--cream-50)] text-[11px] uppercase tracking-wider mb-2">&nbsp;</h5>
          <ul className="space-y-1.5 text-[12.5px] text-[var(--cream-200)]">
            {[
              { label: "Modalidades", id: "modalidades" },
              { label: "Precios", id: "precios" },
              { label: "FAQ", id: "faq" },
              { label: "Contacto", id: "contacto" },
            ].map((t) => (
              <li key={t.id}><a href={`#${t.id}`} className="hover:text-[var(--terra-400)] transition-colors">{t.label}</a></li>
            ))}
            <li className="pt-1 flex items-center gap-1.5 text-[var(--terra-400)]"><Phone size={12} /><a href="tel:+56995081402" className="hover:text-[var(--cream-50)] transition-colors">+56 9 9508 1402</a></li>
            <li className="flex items-center gap-1.5 text-[var(--terra-400)]"><Mail size={12} /><a href="mailto:psandreamartinezlillo@gmail.com" className="hover:text-[var(--cream-50)] transition-colors text-[11px]">psandreamartinezlillo@gmail.com</a></li>
          </ul>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10 pt-3 text-[11px] text-[var(--cream-300)] flex flex-col gap-1.5">
        <span>© 2024 Psicóloga Andrea Martínez. Todos los derechos reservados.</span>
        <div className="flex gap-4 flex-wrap">
          <a href="#" className="hover:text-[var(--cream-50)] transition-colors">Política de privacidad</a>
          <a href="#" className="hover:text-[var(--cream-50)] transition-colors">Términos y condiciones</a>
        </div>
        <span className="flex items-center gap-1">Creado con <Heart size={10} className="text-[var(--terra-400)]" fill="#B86A41" /> para tu bienestar</span>
      </div>
    </div>

    {/* ── ESCRITORIO: layout completo ── */}
    <div className="hidden lg:block">
      <div className="max-w-[1320px] mx-auto px-12 grid grid-cols-[1.2fr_0.8fr_1fr_1fr_1fr] gap-8 mb-6">
        <div>
          <div className="mb-5"><Logo light /></div>
          <p className="text-[var(--cream-200)] text-[13.5px] leading-relaxed max-w-[280px] mb-5">
            Acompaño con cercanía y respeto en tu proceso terapéutico, para que puedas comprenderte,
            sanar y avanzar a tu ritmo.
          </p>
          <div className="flex gap-3 flex-wrap">
            <a href="https://www.instagram.com/psico.andreaml/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[var(--terra-500)] text-white flex items-center justify-center hover:bg-[var(--terra-400)] transition-colors" aria-label="Instagram"><Instagram size={15} /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-[var(--terra-500)] text-white flex items-center justify-center hover:bg-[var(--terra-400)] transition-colors" aria-label="Facebook"><Facebook size={15} /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-[var(--terra-500)] text-white flex items-center justify-center hover:bg-[var(--terra-400)] transition-colors" aria-label="Youtube"><Youtube size={15} /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-[var(--terra-500)] text-white flex items-center justify-center hover:bg-[var(--terra-400)] transition-colors" aria-label="LinkedIn"><Linkedin size={15} /></a>
            <a href="https://www.doctoralia.cl/andrea-martinez-lillo/psicologo/quilpue" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[var(--terra-500)] text-white flex items-center justify-center hover:bg-[var(--terra-400)] transition-colors font-bold text-[13px] tracking-tight" aria-label="Doctoralia">Dr</a>
          </div>
        </div>
        <div>
          <h5 className="font-semibold text-[var(--cream-50)] mb-4">Navegación</h5>
          <ul className="space-y-2 text-[13.5px] text-[var(--cream-200)]">
            {[
              { label: "Inicio", id: "inicio" }, { label: "Sobre mí", id: "sobre" },
              { label: "Mi enfoque", id: "enfoque" }, { label: "Testimonios", id: "testimonios" },
              { label: "Especialidades", id: "especialidades" }, { label: "Modalidad de atención", id: "modalidades" },
              { label: "Precios", id: "precios" }, { label: "Preguntas frecuentes", id: "faq" },
              { label: "Contacto", id: "contacto" },
            ].map((t) => (
              <li key={t.id}><a href={`#${t.id}`} className="hover:text-[var(--terra-400)] transition-colors">{t.label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h5 className="font-semibold text-[var(--cream-50)] mb-4">Atención</h5>
          <div className="space-y-4 text-[13.5px] text-[var(--cream-200)]">
            <div className="flex items-start gap-3"><Laptop2 size={16} className="text-[var(--terra-400)] mt-0.5" />
              <div><div className="text-[var(--cream-50)] font-medium">Sesiones online</div>Desde cualquier lugar</div>
            </div>
            <div className="flex items-start gap-3"><MapPin size={16} className="text-[var(--terra-400)] mt-0.5" />
              <div><div className="text-[var(--cream-50)] font-medium">Atención presencial</div>Providencia, Ñuñoa, Quilpué y Las Condes</div>
            </div>
          </div>
        </div>
        <div>
          <h5 className="font-semibold text-[var(--cream-50)] mb-4">¿Tienes dudas?</h5>
          <p className="text-[var(--cream-200)] text-[13.5px] mb-4">Estoy aquí para ayudarte.<br />Escríbeme y conversemos.</p>
          <div className="space-y-3 text-[13.5px]">
            <a href="tel:+56995081402" className="flex items-center gap-2 text-[var(--terra-400)] hover:text-[var(--cream-50)] transition-colors"><Phone size={14} /> +56 9 9508 1402</a>
            <a href="#" className="flex items-center gap-2 text-[var(--terra-400)] hover:text-[var(--cream-50)] transition-colors"><Mail size={14} /> psandreamartinezlillo@gmail.com</a>
          </div>
        </div>
        <div>
          <Quote size={24} className="text-[var(--terra-400)] mb-3" />
          <p className="font-serif-display italic text-[var(--cream-100)] text-[18px] leading-snug">
            Un pequeño paso hoy puede cambiar tu mañana.
          </p>
          <Heart size={18} className="text-[var(--terra-400)] mt-4" />
        </div>
      </div>
      <div className="border-t border-white/10 pt-4 max-w-[1320px] mx-auto px-12 flex flex-col md:flex-row justify-between gap-3 text-[12.5px] text-[var(--cream-300)]">
        <span>© 2024 Psicóloga Andrea Martínez. Todos los derechos reservados.</span>
        <div className="flex gap-5">
          <a href="#" className="hover:text-[var(--cream-50)] transition-colors">Política de privacidad</a>
          <a href="#" className="hover:text-[var(--cream-50)] transition-colors">Términos y condiciones</a>
          <a href="#" className="hover:text-[var(--cream-50)] transition-colors">Aviso legal</a>
        </div>
        <span className="flex items-center gap-1">Sitio web creado con <Heart size={12} className="text-[var(--terra-400)]" fill="#B86A41" /> para tu bienestar</span>
      </div>
    </div>
  </footer>
);

/* --- SPLASH --- */
const SplashScreen = ({ onDone }) => {
  const [exiting, setExiting] = useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setExiting(true), 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[9999] overflow-hidden flex flex-col items-center justify-center ${exiting ? "splash-exit" : ""}`}
      style={{ background: "var(--cream-50)" }}
      onAnimationEnd={exiting ? onDone : undefined}
    >
      {/* Manchas suaves de color en esquinas */}
      <div className="absolute -top-24 -left-24 w-[380px] h-[380px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, #EAD0B5 0%, transparent 68%)", opacity: 0.55 }} />
      <div className="absolute -bottom-32 -right-32 w-[440px] h-[440px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, #DCC4A5 0%, transparent 68%)", opacity: 0.45 }} />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, #F1DECB 0%, transparent 65%)", opacity: 0.4 }} />

      {/* Botánicas laterales sutiles */}
      <LeafBranch className="absolute left-0 top-0 opacity-20 decor-sway" style={{ height: 340 }} />
      <LeafBranch className="absolute right-0 bottom-0 opacity-15 decor-sway" flip style={{ animationDelay: "1.8s", height: 300 }} />

      {/* Florales esquinas */}
      <FloralCircle className="absolute left-[6%] bottom-[16%] opacity-22 decor-spin" style={{ width: 110, height: 110 }} />
      <FloralCircle className="absolute right-[7%] top-[14%] opacity-18 decor-spin" style={{ animationDelay: "4s", width: 90, height: 90 }} />
      <DotCluster className="absolute right-[18%] bottom-[22%] opacity-30 decor-pulse" />
      <DotCluster className="absolute left-[18%] top-[20%] opacity-25 decor-pulse" style={{ animationDelay: "0.8s" }} />

      {/* Contenido centrado */}
      <div className="splash-content flex flex-col items-center text-center px-8 max-w-[520px] w-full">

        {/* Línea vertical superior */}
        <div className="w-[1px] h-14 mb-7 rounded-full"
          style={{ background: "linear-gradient(to bottom, transparent 0%, var(--terra-400) 100%)", opacity: 0.7 }} />

        {/* Eyebrow */}
        <p className="eyebrow mb-7" style={{ letterSpacing: "0.35em", color: "var(--terra-500)" }}>
          Bienvenida
        </p>

        {/* Título grande */}
        <h1 className="font-serif-display text-[52px] sm:text-[64px] lg:text-[72px] leading-[1.08] text-[var(--ink-900)] mb-8">
          Psicóloga
          <br />
          <span className="italic text-[var(--terra-500)]">Andrea</span> Martínez
        </h1>

        {/* Separador con diamante */}
        <div className="flex items-center gap-5 w-full max-w-[380px] mb-8">
          <div className="flex-1 h-[1px] opacity-50 rounded-full"
            style={{ background: "linear-gradient(to right, transparent, var(--terra-400))" }} />
          <div
            className="shrink-0"
            style={{
              width: 9, height: 9,
              background: "var(--terra-500)",
              transform: "rotate(45deg)",
              opacity: 0.85,
            }}
          />
          <div className="flex-1 h-[1px] opacity-50 rounded-full"
            style={{ background: "linear-gradient(to left, transparent, var(--terra-400))" }} />
        </div>

        {/* Pill badge */}
        <div className="inline-flex items-center gap-2.5 px-7 py-2.5 rounded-full border text-[0.75rem] font-medium tracking-[0.16em] uppercase"
          style={{
            borderColor: "var(--ink-900)",
            color: "var(--ink-900)",
            background: "transparent",
          }}>
          Terapia · Psicología · Bienestar
        </div>

        {/* Línea vertical inferior */}
        <div className="w-[1px] h-14 mt-7 rounded-full"
          style={{ background: "linear-gradient(to bottom, var(--terra-400) 0%, transparent 100%)", opacity: 0.7 }} />
      </div>
    </div>
  );
};

/* --- PAGE --- */
/* --- SCROLL DOT NAV --- */
const scrollSections = [
  { id: "inicio",        label: "Inicio" },
  { id: "sobre",         label: "Sobre mí" },
  { id: "diplomados",    label: "Formación" },
  { id: "enfoque",       label: "Mi enfoque" },
  { id: "testimonios",   label: "Testimonios" },
  { id: "especialidades",label: "Especialidades" },
  { id: "modalidades",   label: "Modalidades" },
  { id: "precios",       label: "Precios" },
  { id: "faq",           label: "FAQ" },
  { id: "contacto",      label: "Contacto" },
];

const ScrollDotNav = () => {
  const [active, setActive] = useState("inicio");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 300);
      let current = "inicio";
      for (const { id } of scrollSections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 160) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      aria-label="Navegación por secciones"
      className={`fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-center gap-3 transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {scrollSections.map(({ id, label }) => {
        const isActive = active === id;
        return (
          <a
            key={id}
            href={`#${id}`}
            aria-label={label}
            title={label}
            className="group relative flex items-center justify-center"
          >
            {/* Dot */}
            <span
              className={`block rounded-full transition-all duration-300 ${
                isActive
                  ? "w-2.5 h-2.5 bg-[var(--terra-500)]"
                  : "w-1.5 h-1.5 bg-[var(--ink-400)] opacity-40 group-hover:opacity-80 group-hover:bg-[var(--terra-400)]"
              }`}
            />
            {/* Tooltip izquierda */}
            <span className="absolute right-5 bg-[var(--ink-900)]/80 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-all duration-200 pointer-events-none">
              {label}
            </span>
          </a>
        );
      })}
    </nav>
  );
};

/* ── GUÍA DE SCROLL MOBILE ─────────────────────────────────────────── */
const MobileScrollGuide = () => {
  const [progress, setProgress] = useState(0);
  const [active, setActive]     = useState("inicio");
  const [visible, setVisible]   = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrolled  = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(maxScroll > 0 ? Math.min(100, (scrolled / maxScroll) * 100) : 0);

      let current = "inicio";
      for (const { id } of scrollSections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 130) current = id;
      }
      setActive(current);
      setVisible(scrolled > 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const currentLabel = scrollSections.find((s) => s.id === active)?.label ?? "";
  const currentIndex = scrollSections.findIndex((s) => s.id === active);

  return (
    <>
      {/* Barra de progreso — debajo del nav fijo */}
      <div className="lg:hidden fixed top-[56px] left-0 right-0 z-40 h-[3px] bg-[var(--cream-300)]">
        <div
          className="h-full bg-[var(--terra-500)] transition-[width] duration-150 ease-out rounded-r-full"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Píldora sección — siempre visible al hacer scroll */}
      <div
        className={`lg:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40 transition-all duration-300 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
      >
        <div
          className="flex items-center gap-2.5 text-white text-[12px] font-semibold px-4 py-2 rounded-full shadow-[0_4px_20px_-4px_rgba(59,42,26,0.45)] whitespace-nowrap"
          style={{ background: "rgba(44,26,16,0.88)", backdropFilter: "blur(8px)" }}
        >
          {/* Dots */}
          <div className="flex gap-1 items-center">
            {scrollSections.map((s, i) => (
              <span
                key={s.id}
                className={`rounded-full transition-all duration-250 ${
                  i === currentIndex
                    ? "w-2.5 h-2.5 bg-[var(--terra-400)]"
                    : "w-1.5 h-1.5 bg-white/30"
                }`}
              />
            ))}
          </div>
          <span className="border-l border-white/30 pl-2.5 tracking-wide">{currentLabel}</span>
        </div>
      </div>
    </>
  );
};

/* Wrapper que revela su contenido al entrar en el viewport */
const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.06, rootMargin: "0px 0px -48px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

const Landing = () => {
  const [splash, setSplash]     = useState(true);

  return (
  <div className="bg-[var(--cream-50)] text-[var(--ink-900)] min-h-screen" data-testid="landing-page">
    {splash && <SplashScreen onDone={() => setSplash(false)} />}
    <Nav />
    <main>
      <Hero />
      <Reveal><Proceso /></Reveal>
      <Reveal delay={60}><Diplomados /></Reveal>
      <Reveal delay={60}><Enfoque /></Reveal>
      <Reveal><Testimonios /></Reveal>
      <Reveal delay={60}><Especialidades /></Reveal>
      <Reveal><Modalidades /></Reveal>
      <Reveal delay={60}><Precios /></Reveal>
      <Reveal><FAQ /></Reveal>
      <Reveal delay={60}><Contacto /></Reveal>
    </main>
    <Footer />
    <ScrollDotNav />
    <MobileScrollGuide />

    {/* Botón flotante WhatsApp */}
    <a
      href="https://wa.me/56995081402"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Contactar por WhatsApp"
      className="fixed bottom-6 right-6 z-50 group"
    >
      {/* Pulso animado de fondo */}
      <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-30 scale-100 group-hover:scale-125 group-hover:opacity-0 transition-all duration-500 animate-ping" />
      {/* Botón principal */}
      <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-[0_8px_24px_-4px_rgba(37,211,102,0.55)] group-hover:scale-110 group-hover:shadow-[0_12px_32px_-4px_rgba(37,211,102,0.65)] transition-all duration-300">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </span>
      {/* Tooltip */}
      <span className="absolute right-16 top-1/2 -translate-y-1/2 bg-[var(--ink-900)] text-white text-[12px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300 pointer-events-none shadow-md">
        ¿Hablamos?
      </span>
    </a>
  </div>
  );
};

export default Landing;
